import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Modal } from 'antd';
import Select from 'react-select';
import '../../assets/styles/seller_profile.scss';
import '../../assets/styles/contract_manufacturer_profile.scss';
import CommonService from '../../services/common.service';
import { toast } from 'react-toastify';
import { API_URLS, resolvePublicFileUrl } from '../../constants/api-urls';
import { INDUSTRY_TYPE_MAP } from '../../constants/industryTypeMap';
import cmIndustryTypeService from '../../services/cmIndustryType.service';
import { CONTRACT_MANUFACTURING_TYPE_MAP } from '../../constants/contractManufacturingTypeMap';
import { FACILITY_TYPE_MAP } from '../../constants/facilityTypeMap';
import { useLocationMaster } from '../../hooks/useLocationMaster';
import { useProductDetailsMaster } from '../../hooks/useProductDetailsMaster';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import {
  MANUFACTURING_PROCESS_TYPE_OPTIONS,
  PROCESS_OPTIONS,
  LABEL_OPTIONS,
  TECHNICAL_EXPERTISE_OPTIONS,
  BATCH_SIZE_OPTIONS,
  MACHINE_CAPACITY_UNIT_OPTIONS,
  MACHINE_CAPACITY_PERIOD_OPTIONS,
  OPERATIONAL_STATUS_OPTIONS,
} from '../../constants/manufacturingCapabilitiesMap';
import { getPriceUomOptions } from '../../constants/pricingCommercialMap';
import {
  FACILITY_AREA_UNIT_OPTIONS,
  normalizeFacilityAreaFields,
  resolveFacilityAreaUnit,
} from '../../constants/facilityAreaUnits';
import {
  COLD_STORAGE_UNIT_OPTIONS,
  normalizeStorageFacilityFields,
  resolveColdStorageUnit,
  resolveStorageWarehouseUnit,
  STORAGE_WAREHOUSE_UNIT_OPTIONS,
} from '../../constants/storageFacilityUnits';

const OWNERSHIP_TYPE_OPTIONS = [
  { value: 'owned', label: 'Owned' },
  { value: 'sub_leased', label: 'Sub Leased' },
  { value: 'joint_venture', label: 'Joint Venture' },
  { value: 'financed', label: 'Financed' },
  { value: 'leased', label: 'Leased' },
];
const LEASE_TYPE_OPTIONS = [
  { value: 'short_term', label: 'Short term' },
  { value: 'long_term', label: 'Long Term' },
  { value: 'renewable', label: 'Renewable' },
  { value: 'flexible_terms', label: 'Flexible terms' },
];
const INSURANCE_COVERAGE_OPTIONS = [
  { value: 'fire', label: 'Fire' },
  { value: 'theft', label: 'Theft' },
  { value: 'third_party_liability', label: 'Third-Party Liability' },
  { value: 'natural_disasters', label: 'Natural Disasters' },
  { value: 'accidental_damage', label: 'Accidental Damage' },
  { value: 'comprehensive', label: 'Comprehensive' },
];
const REGULATORY_APPROVALS_OPTIONS = [
  { value: 'factory_license', label: 'Factory License (Local Government Authority)' },
  { value: 'pollution_control_clearance', label: 'Pollution Control Board Clearance (State/Local Authority)' },
  { value: 'fire_safety_clearance', label: 'Fire Safety Clearance (Fire Department)' },
  { value: 'environmental_clearance', label: 'Environmental Clearance (Ministry of Environment & Forest)' },
  { value: 'trade_license', label: 'Trade License (Local Municipal Authority)' },
  { value: 'gst_registration', label: 'GST Registration (Goods and Services Tax)' },
  { value: 'labour_license', label: 'Labour License (Labour Department)' },
];
const COMPLIANCE_CERTIFICATE_OPTIONS = [
  { value: 'iso_9001', label: 'ISO 9001: Quality Management System (For General Manufacturing)' },
  { value: 'iso_45001', label: 'ISO 45001: Occupational Health and Safety Management System' },
  { value: 'gmp_certification', label: 'GMP Certification (For Pharmaceutical Manufacturing)' },
  { value: 'fssai_certificate', label: 'FSSAI Certificate (For Food Manufacturing)' },
  { value: 'ce_marking', label: 'CE Marking (For Electrical/Electronic Products)' },
  { value: 'isi_mark', label: 'ISI Mark (For Products under BIS Standards)' },
];
const TYPE_OF_CONSTRUCTION_OPTIONS = [
  { value: 'peb', label: 'PEB' },
  { value: 'rcc', label: 'RCC' },
  { value: 'modular', label: 'Modular' },
  { value: 'prefabricated', label: 'Prefabricated' },
];
const ACCESSIBILITY_OPTIONS = [
  { value: 'road', label: 'Road' },
  { value: 'rail', label: 'Rail' },
  { value: 'air', label: 'Air' },
  { value: 'water', label: 'Water' },
];
const CONDITION_OF_FACILITY_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'needs_maintenance', label: 'Needs Maintenance' },
  { value: 'under_renovation', label: 'Under Renovation' },
];
// Product Details - Production Capacity: number + unit + period (design)
const PRODUCTION_CAPACITY_UNIT_OPTIONS = [
  { value: 'units', label: 'Units' },
  { value: 'tonnes', label: 'Tonnes' },
];
const PRODUCTION_CAPACITY_PERIOD_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'months', label: 'Months' },
];
const PACKAGING_OPTIONS_PRODUCT = [
  { value: '', label: 'Packaging Options' },
  { value: 'bulk', label: 'Bulk' },
  { value: 'bottles', label: 'Bottles' },
  { value: 'pouches', label: 'Pouches' },
  { value: 'cartons', label: 'Cartons' },
  { value: 'custom', label: 'Custom' },
];
const PRODUCT_TYPE_OPTIONS = [
  { value: '', label: 'Product Type' },
  { value: 'finished_good', label: 'Finished Good' },
  { value: 'semi_finished', label: 'Semi-finished' },
  { value: 'raw_material', label: 'Raw Material' },
  { value: 'component', label: 'Component' },
];
const PRICING_MODEL_OPTIONS = [
  { value: '', label: 'Choose Pricing Model' },
  { value: 'per_piece_unit_price', label: 'Per Piece ( Unit Price )' },
  { value: 'per_batch_lot', label: 'Per Batch/ Lot' },
  { value: 'per_hour_machine_labour', label: 'Per Hour ( Machine / Labour )' },
  { value: 'per_day_per_shift', label: 'Per Day / Per Shift' },
  { value: 'monthly_lease_retainer', label: 'Monthly Lease / Retainer' },
  { value: 'cost_bom_pass_through_markup', label: 'Cost + ( BOM Pass Through + Markup)' },
  { value: 'fixed_project_fee', label: 'Fixed project Fee' },
];
const MARKUP_BASIS_OPTIONS = [
  { value: '', label: 'Markup Basis' },
  { value: 'percent_bom_cost', label: '% of BOM Cost' },
  { value: 'fixed_per_unit', label: 'Fixed Per unit' },
  { value: 'fixed_per_batch', label: 'Fixed Per Batch' },
];
const INCOTERMS_OPTIONS = [
  { value: '', label: 'Incoterms' },
  { value: 'exw', label: 'EXW' },
  { value: 'fca', label: 'FCA' },
  { value: 'cpt', label: 'CPT' },
  { value: 'cip', label: 'CIP' },
  { value: 'dap', label: 'DAP' },
  { value: 'dpu', label: 'DPU' },
  { value: 'ddp', label: 'DDP' },
  { value: 'fas', label: 'FAS' },
  { value: 'fob', label: 'FOB' },
  { value: 'cfr', label: 'CFR' },
  { value: 'cif', label: 'CIF' },
];
const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Payment Terms' },
  { value: 'advance', label: 'Advance' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
  { value: 'net_90', label: 'Net 90' },
  { value: 'cod', label: 'COD' },
  { value: 'lc', label: 'Letter of Credit' },
  { value: 'custom', label: 'Custom' },
];
const RENEWAL_TERMS_OPTIONS = [
  { value: '', label: 'Renewal Terms' },
  { value: 'auto_renew_monthly', label: 'Auto-Renew (Monthly)' },
  { value: 'auto_renew_annual', label: 'Auto-Renew (Annual)' },
  { value: 'manual_renewal', label: 'Manual Renewal' },
  { value: 'no_renewal', label: 'No Renewal' },
];
const TERMINATION_TERMS_OPTIONS = [
  { value: '', label: 'Termination Terms' },
  { value: '30_days_notice', label: '30 days Notice' },
  { value: '60_days_notice', label: '60 days notice' },
  { value: '90_days_notice', label: '90 days notice' },
  { value: 'mutual_agreement', label: 'Mutual agreement' },
  { value: 'early_termination_fee_applies', label: 'Early Termination fee Applies' },
];
const RESPONSIBILITY_OPTIONS = [
  { value: '', label: 'Select' },
  { value: 'owner', label: 'Owner' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'shared', label: 'Shared' },
];
const ESCALATION_BASIS_OPTIONS = [
  { value: '', label: 'Escalation Basis' },
  { value: 'none', label: 'None' },
  { value: 'cpi_index', label: 'CPI Index' },
  { value: 'lme_metals', label: 'LME (Metals)' },
  { value: 'resin_index', label: 'Resin Index (Chem/ Plastics)' },
  { value: 'energy_surcharge', label: 'Energy Surcharge' },
  { value: 'fx_clause', label: 'FX Clause' },
];
const ADD_ON_CATEGORIES_OPTIONS = [
  { value: '', label: 'Add on Categories' },
  { value: 'setup_changeover_per_run', label: 'Setup/Changeover per run' },
  { value: 'packaging_materials', label: 'Packaging materials' },
  { value: 'qa_inspection_fai_ppap', label: 'QA/Inspection (FAI/PPAP)' },
  { value: 'certification_documents', label: 'Certification documents' },
  { value: 'freight_logistics', label: 'Freight & Logistics' },
  { value: 'duties_taxes', label: 'Duties/Taxes' },
  { value: 'artwork_tool_texturing', label: 'Artwork/tool texturing' },
  { value: 'expedite_rush_fee', label: 'Expedite/Rush fee' },
  { value: 'disposal_waste_handling', label: 'Disposal/Waste handling' },
];
// Primary Power Source options (dropdown map)
const PRIMARY_POWER_SOURCE_OPTIONS = [
  { value: 'electricity_board', label: 'Electricity Board' },
  { value: 'solar_power', label: 'Solar Power' },
  { value: 'generator', label: 'Generator' },
  { value: 'hybrid', label: 'Hybrid' },
];
// Sanctioned Power Load unit options (number + unit design)
const SANCTIONED_POWER_LOAD_UNIT_OPTIONS = [
  { value: 'kva', label: 'KVA' },
  { value: 'mw', label: 'MW' },
];
// Water Supply Capacity unit options (number + unit design)
const WATER_SUPPLY_CAPACITY_UNIT_OPTIONS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
];
// Fire & Safety Equipment options (multi-select, map only - no number input)
const FIRE_SAFETY_EQUIPMENT_OPTIONS = [
  { value: 'fire_extinguishers', label: 'Fire Extinguishers (ABC / CO2 / Foam / Water)' },
  { value: 'fire_hydrant_system', label: 'Fire Hydrant System' },
  { value: 'automatic_sprinkler_system', label: 'Automatic Sprinkler System' },
  { value: 'fire_alarm_system', label: 'Fire Alarm System' },
  { value: 'smoke_heat_detectors', label: 'Smoke Detectors / Heat Detectors' },
  { value: 'hose_reels', label: 'Hose Reels' },
  { value: 'emergency_exit_signage', label: 'Emergency Exit Signage' },
  { value: 'emergency_lighting', label: 'Emergency Lighting' },
  { value: 'first_aid_kit', label: 'First Aid Kit' },
  { value: 'ppe', label: 'Personal Protective Equipment (PPE)' },
  { value: 'safety_showers_eye_wash', label: 'Safety Showers & Eye Wash Stations' },
  { value: 'gas_leak_detectors', label: 'Gas Leak Detectors' },
  { value: 'control_panels', label: 'Control Panels (Fire Alarm / Safety Monitoring)' },
];
// Distance unit options for Supply Chain Logistics (same as seller profile factory distance)
const DISTANCE_UNIT_OPTIONS = [
  { value: 'km', label: 'KM' },
  { value: 'miles', label: 'Miles' },
  { value: 'meters', label: 'Meters' },
  { value: 'feet', label: 'Feet' },
  { value: 'inches', label: 'Inches' },
  { value: 'yards', label: 'Yards' },
];
// Storage & cold unit options — see constants/storageFacilityUnits.js
// Unit options for Total Facility Area & Build-Up Area — see constants/facilityAreaUnits.js

let TabPane;
if (Tabs.TabPane) {
  TabPane = Tabs.TabPane;
} else {
  TabPane = ({ children, ...props }) => <div {...props}>{children}</div>;
}

/** Numeric input (placeholder) + unit dropdown (right). */
function NumberWithUnit({
  placeholder,
  ariaLabel,
  value,
  unit,
  unitOptions,
  onValueChange,
  onUnitChange,
  inputId,
  resolveUnit,
  defaultUnit,
}) {
  const fallbackUnit = defaultUnit || unitOptions[0]?.value || '';
  const resolvedUnit = resolveUnit ? resolveUnit(unit || fallbackUnit) : (unit || fallbackUnit);
  return (
    <div className="input-group-prefix-suffix input-group-row cm-number-unit">
      <input
        id={inputId}
        type="number"
        min="0"
        step="any"
        className="input-middle input-middle-with-border"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        aria-label={ariaLabel || placeholder}
      />
      <div className="input-prefix input-prefix-no-borders input-unit-suffix">
        <select
          value={resolvedUnit}
          onChange={(e) => onUnitChange(e.target.value)}
          aria-label={`${ariaLabel || placeholder} unit`}
        >
          {unitOptions.map((u) => (
            <option key={u.value} value={u.value}>{u.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Numeric input (placeholder) + fixed % suffix on the right. */
function PercentInput({ placeholder, ariaLabel, value, onChange, inputId }) {
  return (
    <div className="input-group-prefix-suffix input-group-row cm-number-unit">
      <input
        id={inputId}
        type="number"
        min="0"
        max="100"
        step="any"
        className="input-middle input-middle-with-border"
        placeholder={placeholder}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel || placeholder}
      />
      <div className="input-prefix input-prefix-no-borders input-unit-suffix cm-percent-suffix">
        <span aria-hidden="true">%</span>
      </div>
    </div>
  );
}

/** Multi-select with Select All / Deselect All (design checklist pattern). */
function CapabilityMultiSelect({ placeholder, options, value = [], onChange }) {
  const selected = options.filter((o) => (value || []).includes(o.value));
  const allValues = options.map((o) => o.value);
  const allSelected = allValues.length > 0 && allValues.every((v) => (value || []).includes(v));
  const selectAllOption = { value: '__select_all__', label: allSelected ? 'Deselect All' : 'Select All' };

  const handleChange = (opts) => {
    if (!opts) {
      onChange([]);
      return;
    }
    if (opts.some((o) => o.value === '__select_all__')) {
      onChange(allSelected ? [] : allValues);
      return;
    }
    onChange(opts.filter((o) => o.value !== '__select_all__').map((o) => o.value));
  };

  return (
    <Select
      className="input-pill p-0 cm-select"
      isMulti
      placeholder={placeholder}
      options={[selectAllOption, ...options]}
      value={selected}
      onChange={handleChange}
      isClearable
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
    />
  );
}

function MachineCapacityInput({ value, unit, period, onValueChange, onUnitChange, onPeriodChange }) {
  return (
    <div className="input-group-prefix-suffix input-group-row cm-number-unit cm-machine-capacity">
      <input
        type="number"
        min="0"
        step="any"
        className="input-middle input-middle-with-border"
        placeholder="Machine Capacity"
        value={value ?? ''}
        onChange={(e) => onValueChange(e.target.value)}
        aria-label="Machine Capacity"
      />
      <div className="input-prefix input-prefix-no-borders input-unit-suffix">
        <select
          value={unit || ''}
          onChange={(e) => onUnitChange(e.target.value)}
          aria-label="Machine capacity unit"
        >
          <option value="">Unit</option>
          {MACHINE_CAPACITY_UNIT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="input-prefix input-prefix-no-borders input-unit-suffix cm-machine-capacity-period">
        <select
          value={period || ''}
          onChange={(e) => onPeriodChange(e.target.value)}
          aria-label="Machine capacity period"
        >
          <option value="">Per</option>
          {MACHINE_CAPACITY_PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

function ProductionCapacityInput({ value, unit, period, onValueChange, onUnitChange, onPeriodChange }) {
  return (
    <div className="input-group-prefix-suffix input-group-row cm-number-unit cm-production-capacity">
      <input
        type="number"
        min="0"
        step="any"
        className="input-middle input-middle-with-border"
        placeholder="Production Capacity"
        value={value ?? ''}
        onChange={(e) => onValueChange(e.target.value)}
        aria-label="Production Capacity"
      />
      <div className="input-prefix input-prefix-no-borders input-unit-suffix">
        <select
          value={unit || ''}
          onChange={(e) => onUnitChange(e.target.value)}
          aria-label="Production capacity unit"
        >
          <option value="" disabled hidden>Select unit</option>
          {PRODUCTION_CAPACITY_UNIT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="input-prefix input-prefix-no-borders input-unit-suffix cm-production-capacity-period">
        <select
          value={period || ''}
          onChange={(e) => onPeriodChange(e.target.value)}
          aria-label="Production capacity period"
        >
          <option value="" disabled hidden>Select period</option>
          {PRODUCTION_CAPACITY_PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

const defaultFacility = {
  companyName: '',
  facilityName: '',
  companyRegNo: '',
  industryTypes: [],
  contractManufacturingType: '',
  facilityType: '',
  factoryLicenceNumber: '',
  factoryLicenceValidityPeriod: '',
  taxId: '',
  exportImportCode: '',
  parentCompanyOrGroupOwnership: '',
  address: '',
  country: '',
  state: '',
  city: '',
  /** Human-readable labels for public listing / APIs (IDs stay in country, state, city) */
  countryName: '',
  stateName: '',
  cityName: '',
  pincode: '',
  facilityImages: [],
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contactDesignation: '',
  altContactName: '',
  altContactEmail: '',
  altContactPhone: '',
  altContactDesignation: '',
  ownershipType: '',
  leaseType: '',
  insuranceCoverage: [], // multi-select
  regulatoryApprovals: [], // multi-select
  complianceCertificate: [], // multi-select (certificate types)
  complianceCertificates: [], // uploaded certificate file URLs
  environmentalClearances: true,
  typeOfConstruction: '',
  numberOfFloors: '',
  yearOfConstruction: '',
  totalFacilityArea: '',
  totalFacilityAreaUnit: 'sq_feet',
  accessibility: '',
  buildUpArea: '',
  buildUpAreaUnit: 'sq_feet',
  conditionOfFacility: '',
  primaryPowerSource: '',
  sanctionedPowerLoad: '',
  sanctionedPowerLoadUnit: 'kva',
  powerSupplyCapacity: '',
  powerSupplyCapacityUnit: 'kva',
  powerType: '',
  waterSupplyCapacity: '',
  waterSupplyCapacityUnit: 'hour',
  wasteWaterTreatment: false,
  wasteWaterTreatmentDetails: '',
  powerBackup: false,
  wasteManagementSystem: false,
  wasteManagementSystemDetails: '',
  rawMaterialWarehouse: '',
  rawMaterialWarehouseUnit: 'sq_ft',
  coldStorage: '',
  coldStorageUnit: 'mt',
  finishedGoodsStorage: '',
  finishedGoodsStorageUnit: 'sq_ft',
  fireSafetyEquipments: [], // multi-select (map only)
  portName: '',
  portDistanceKm: '',
  portDistanceUnit: 'km',
  airportName: '',
  airportDistanceKm: '',
  airportDistanceUnit: 'km',
  highwayName: '',
  highwayDistanceKm: '',
  highwayDistanceUnit: 'km',
  annualTurnoverBracket: '',
  exportDomesticSplit: '',
  majorClients: '',
  riskIndicator: true,
  majorClientLogo: '',
  // Public Contract Manufacturers directory (optional; used on browse cards)
  listingFeatured: false,
  ratingScore: '',
  reviewCount: '',
  averageResponseTimeHours: '',
  completedOrders: '',
  monthlyProductionCapacity: '',
};

const FACILITY_DETAILS_ENTRY_FIELDS = [
  'companyName', 'facilityName', 'companyRegNo', 'industryTypes',
  'contractManufacturingType', 'facilityType', 'factoryLicenceNumber',
  'factoryLicenceValidityPeriod', 'taxId', 'exportImportCode',
  'parentCompanyOrGroupOwnership', 'address', 'country', 'state', 'city', 'pincode', 'facilityImages',
];

const OWNERSHIP_COMPLIANCE_ENTRY_FIELDS = [
  'ownershipType', 'leaseType', 'insuranceCoverage', 'regulatoryApprovals',
  'complianceCertificate', 'environmentalClearances', 'complianceCertificates',
];

const cloneDefaultValue = (value) => (Array.isArray(value) ? [] : value);

const createFacilityDetailsEntry = (source = {}) => {
  const entry = {};
  FACILITY_DETAILS_ENTRY_FIELDS.forEach((key) => {
    entry[key] = source[key] !== undefined && source[key] !== null
      ? source[key]
      : cloneDefaultValue(defaultFacility[key]);
  });
  if (!Array.isArray(entry.facilityImages)) entry.facilityImages = [];
  if (!Array.isArray(entry.industryTypes)) entry.industryTypes = [];
  return entry;
};

const createOwnershipComplianceEntry = (source = {}) => {
  const entry = {};
  OWNERSHIP_COMPLIANCE_ENTRY_FIELDS.forEach((key) => {
    entry[key] = source[key] !== undefined && source[key] !== null
      ? source[key]
      : cloneDefaultValue(defaultFacility[key]);
  });
  ['insuranceCoverage', 'regulatoryApprovals', 'complianceCertificate', 'complianceCertificates'].forEach((key) => {
    if (!Array.isArray(entry[key])) entry[key] = [];
  });
  if (typeof entry.environmentalClearances !== 'boolean') entry.environmentalClearances = true;
  return entry;
};

const PRODUCTION_LINE_LAYOUTS_OPTIONS = [
  { value: 'linear', label: 'Linear' },
  { value: 'u_shaped', label: 'U Shaped' },
  { value: 'l_shaped', label: 'L Shaped' },
];
const AUTOMATION_LEVEL_OPTIONS = [
  { value: '', label: 'Automation Level' },
  { value: 'manual', label: 'Manual' },
  { value: 'semi_automated', label: 'Semi-Automated' },
  { value: 'automated', label: 'Automated' },
  { value: 'fully_automated', label: 'Fully Automated' },
];
const MAINTENANCE_SCHEDULE_OPTIONS = [
  { value: '', label: 'Maintenance Schedule' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi_annually', label: 'Semi Annually' },
  { value: 'annually', label: 'Annually' },
];
const DOWNTIME_OPTIONS = [
  { value: '', label: 'Downtime' },
  { value: 'hours_per_day', label: 'Hours per Day' },
  { value: 'hours_per_month', label: 'Hours per month' },
];

const selectOptionsForPicker = (options) => options.filter((o) => o.value !== '');
const findSelectOption = (options, value) => {
  const v = String(value ?? '').trim();
  if (!v) return null;
  return options.find((o) => o.value === v) || null;
};

const FUTURE_EXPANSION_UNIT_OPTIONS = [
  { value: 'kg', label: 'Kg' },
  { value: 'litres', label: 'Litres' },
  { value: 'unit', label: 'Unit' },
  { value: 'tables', label: 'Tables' },
  { value: 'bottles', label: 'Bottles' },
];
const FUTURE_EXPANSION_CAPACITY_OPTIONS = [
  { value: 'kg', label: 'Kg' },
  { value: 'litres', label: 'Litres' },
  { value: 'unit', label: 'Unit' },
  { value: 'tables', label: 'Tables' },
  { value: 'bottles', label: 'Bottles' },
];
const FUTURE_EXPANSION_PERIOD_OPTIONS = [
  { value: 'days', label: 'Days' },
  { value: 'month', label: 'month' },
  { value: 'year', label: 'year' },
];
const SUSTAINABILITY_CERTIFICATE_OPTIONS = [
  { value: 'iso_14001', label: 'ISO 14001 (Environmental Management System)' },
  { value: 'iso_50001', label: 'ISO 50001 (Energy Management System)' },
  { value: 'iso_45001', label: 'ISO 45001 (Occupational Health & Safety)' },
  { value: 'sa8000', label: 'SA8000 (Social Accountability)' },
  { value: 'b_corp', label: 'B-Corp / B Corporation' },
  { value: 'fair_trade', label: 'Fair Trade / Fairtrade' },
  { value: 'fsc', label: 'Forest Stewardship Council (FSC)' },
  { value: 'leed', label: 'LEED' },
  { value: 'igbc', label: 'IGBC (Indian Green Building Council)' },
  { value: 'griha', label: 'GRIHA (Green Rating for Integrated Habitat Assessment)' },
  { value: 'ecomark_india', label: 'Ecomark (India)' },
  { value: 'rainforest_alliance', label: 'Rainforest Alliance' },
  { value: 'green_seal', label: 'Green Seal' },
  { value: 'cradle_to_cradle', label: 'Cradle-to-Cradle Certified' },
];
const USE_OF_RENEWABLE_ENERGY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'partial', label: 'Partial' },
  { value: 'fully', label: 'Fully' },
];
const EMISSION_REDUCTION_TARGET_OPTIONS = [
  { value: 'no_target', label: 'No target' },
  { value: 'lt_20_over_baseline', label: '< 20% over baseline' },
  { value: '20_50', label: '20-50%' },
  { value: '50_100', label: '50-100%' },
  { value: 'net_zero', label: 'Net Zero' },
  { value: 'carbon_neutral', label: 'Carbon Neutral' },
];
const WASTE_MANAGEMENT_OPTIONS = [
  { value: 'no_program', label: 'No program' },
  { value: 'basic_recycling', label: 'Basic Recycling' },
  { value: 'waste_segregation', label: 'Waste Segregation' },
  { value: 'zero_waste', label: 'Zero Waste' },
  { value: 'composting', label: 'Composting' },
  { value: 'circular_economy', label: 'Circular Economy' },
  { value: 'initiatives', label: 'Initiatives' },
  { value: 'reuse_upcycling', label: 'Reuse / Upcycling' },
  { value: 'responsible_disposal', label: 'Responsible Disposal' },
  { value: 'procedure', label: 'Procedure' },
];
const PACKAGING_SUSTAINABILITY_OPTIONS = [
  { value: 'recycled_packaging', label: 'Recycled Packaging' },
  { value: 'biodegradable_minimal', label: 'Biodegradable Minimal Packaging' },
  { value: 'returnable_reusable', label: 'Returnable / Reusable' },
  { value: 'compostable', label: 'Compostable' },
  { value: 'plastic_free', label: 'Plastic-Free' },
  { value: 'other_describe', label: 'Other / Describe' },
];
const RESPONSIBLE_SOURCING_OPTIONS = [
  { value: 'material_traceability', label: 'Material Traceability' },
  { value: 'use_of_recycled_materials', label: 'Use of Recycled materials' },
  { value: 'supplier_audits', label: 'Supplier Audits' },
  { value: 'ethical_labor_policies', label: 'Ethical Labor Policies' },
  { value: 'local_sourcing', label: 'Local Sourcing' },
  { value: 'conflict_free_materials', label: 'Conflict-Free Materials' },
  { value: 'certifications_of_suppliers', label: 'Certifications of Suppliers' },
];
const WATER_USE_CONVERSION_OPTIONS = [
  { value: 'no_program', label: 'No Program' },
  { value: 'basic_conservation', label: 'Basic Conservation' },
  { value: 'advanced_conservation', label: 'Advanced Conservation Measures' },
  { value: 'recycled_reused_water', label: 'Recycled / Reused Water' },
  { value: 'rainwater_harvesting', label: 'Rainwater Harvesting' },
  { value: 'wastewater_treatment', label: 'Wastewater Treatment' },
];
const SOCIAL_LABOR_PRACTICES_OPTIONS = [
  { value: 'safe_working_conditions', label: 'Safe Working Conditions' },
  { value: 'fair_wage_policies', label: 'Fair Wage Policies' },
  { value: 'workers_rights_policies', label: "Workers' Rights Policies" },
  { value: 'gender_diversity_equality', label: 'Gender / Diversity Equality' },
  { value: 'community_engagement', label: 'Community Engagement' },
  { value: 'ethical_labor_audits', label: 'Ethical Labor Audits' },
  { value: 'health_safety_training', label: 'Health & Safety Training' },
  { value: 'child_labor_policy', label: 'Child Labor Policy' },
];
const ENVIRONMENTAL_IMPACT_OPTIONS = [
  { value: 'waste_reduction_initiatives', label: 'Waste reduction initiatives' },
  { value: 'energy_efficiency_measures', label: 'Energy efficiency measures' },
  { value: 'water_conservation_practices', label: 'Water conservation practices' },
  { value: 'use_of_renewable_energy', label: 'Use of renewable energy' },
  { value: 'sustainable_packaging', label: 'Sustainable packaging' },
];
const LABOUR_PRACTICES_OPTIONS = [
  { value: 'fair_wages', label: 'Fair wages' },
  { value: 'safe_working_conditions', label: 'Safe working conditions' },
  { value: 'no_child_labor', label: 'No child labor' },
  { value: 'no_forced_labor', label: 'No forced labor' },
  { value: 'non_discrimination', label: 'Non-discrimination' },
  { value: 'freedom_of_association', label: 'Freedom of association' },
];
const AUDIT_COMPLIANCE_OPTIONS = [
  { value: 'internal_audits', label: 'Internal audits' },
  { value: 'third_party_audits', label: 'Third-party audits' },
  { value: 'compliance_local_labor_laws', label: 'Compliance with local labor laws' },
  { value: 'compliance_international_labor_standards', label: 'Compliance with international labor standards' },
];
const QUALITY_CERTIFICATION_AVAILABLE_OPTIONS = [
  { value: 'iso_9001_2015', label: 'ISO 9001:2015 – Quality Management Systems' },
  { value: 'iso_13485_2016', label: 'ISO 13485:2016 – Medical Devices' },
  { value: 'iatf_16949_2016', label: 'IATF 16949:2016 – Automotive Quality Management' },
  { value: 'as9100d', label: 'AS9100D – Aerospace Quality Management' },
  { value: 'iso_14001_2015', label: 'ISO 14001:2015 – Environmental Management Systems' },
  { value: 'iso_45001_2018', label: 'ISO 45001:2018 – Occupational Health & Safety' },
  { value: 'iso_22000_2018', label: 'ISO 22000:2018 – Food Safety Management' },
  { value: 'iso_27001_2013', label: 'ISO 27001:2013 – Information Security Management' },
  { value: 'iso_50001_2018', label: 'ISO 50001:2018 – Energy Management Systems' },
  { value: 'iso_22301_2019', label: 'ISO 22301:2019 – Business Continuity Management' },
  { value: 'iso_20000_1_2018', label: 'ISO 20000-1:2018 – IT Service Management' },
  { value: 'sa_8000_2014', label: 'SA 8000:2014 – Social Accountability' },
  { value: 'ce_marking', label: 'CE Marking – Conformity for Products in the European Market' },
  { value: 'fda_approval', label: 'FDA Approval – U.S. Food and Drug Administration Approval' },
  { value: 'fcc_certification', label: 'FCC Certification – Federal Communications Commission Approval' },
  { value: 'cb_scheme', label: 'CB Scheme – International Electrotechnical Certification' },
];
const INSPECTION_METHODS_OPTIONS = [
  { value: 'visual', label: 'Visual' },
  { value: 'automated', label: 'Automated' },
  { value: 'random', label: 'Random' },
  { value: 'sampling', label: 'Sampling' },
];
const SUSTAINABILITY_SELECT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'planned', label: 'Planned' },
  { value: 'na', label: 'N/A' },
];
const defaultCapabilityEntry = {
  manufacturingProcessType: [],
  processes: [],
  label: [],
  technicalExpertise: [],
  numberOfProductionUnit: '',
  batchSize: '',
  rndCapabilities: true,
  rndCapabilitiesDetails: '',
  machineName: '',
  yearOfPurchase: '',
  makeAndModel: '',
  machineCapacityValue: '',
  machineCapacity: '',
  machineCapacityPeriod: '',
  operationalStatus: [],
};
const defaultProduction = {
  ...defaultCapabilityEntry,
  capabilitiesList: [{ ...defaultCapabilityEntry }],
  productionLineLayouts: '',
  automationLevel: '',
  capacityUtilization: '',
  maintenanceSchedule: '',
  downtime: '',
  rejectionRate: '',
  futureExpansionCapacityValue: '',
  futureExpansionCapacity: '',
  futureExpansionCapacityUnit: '',
  sustainabilityCertificate: [],
  useOfRenewableEnergy: '',
  emissionReductionTarget: '',
  wasteManagement: '',
  packagingSustainability: '',
  responsibleSourcing: [],
  waterUseConversion: '',
  socialLaborPractices: [],
  environmentalImpact: [],
  sustainabilityDescription: '',
  supplierCertifications: [],
  traceabilityReports: [],
  transparencyDocumentation: [],
  labourPractices: [],
  grievanceReportingMechanism: '',
  auditCompliance: [],
  trainingCapacityBuilding: '',
  ethicalDescription: '',
  codeOfConductDocuments: [],
  ethicalSourcingPolicyDocuments: [],
  qualityControlSystem: '',
  qualityCertificationAvailable: [],
  inspectionMethods: '',
  testingFacilities: false,
  testingFacilitiesDetails: '',
  numberPermanentWorkers: '',
  numberContractWorkers: '',
  numberSeasonalWorkers: '',
  skilledWorkerRatio: '',
  safetyIncidentLast12Months: '',
  workerTrainingProgram: false,
  workerTrainingProgramDetails: '',
  onSiteAccommodation: true,
  noOfPeopleCanStay: '',
  emergencyPreparedness: true,
  emergencyPlanDocuments: [],
};

const defaultProduct = {
  productName: '',
  brand: '',
  productCategoryId: '',
  productSubCategoryId: '',
  productSubSubCategoryId: '',
  mainProduct: false,
  hsnCode: '',
  taxCode: '',
  modelNumber: '',
  productDescription: '',
  keywords: [],
  productCatalogueUrl: '',
  productCompanyLogoUrl: '',
  productVideoUrl: '',
  typeOfProductsManufactured: '',
  productCategory: '',
  productSubSubCategory: '',
  productType: '',
  productionCapacity: '',
  productionCapacityValue: '',
  productionCapacityUnit: '',
  productionCapacityPeriod: '',
  packagingOptions: '',
  materialUsed: '',
  customizationAvailable: false,
  customizationType: '',
  minimumOrderQuantity: '',
  moqUom: '',
  moqPriceNotax: '',
  moqTax: '',
  moqPriceTax: '',
  moqCurrency: '',
  leadTimeDays: '',
  pricingModel: '',
  priceAmount: '',
  priceCurrency: '',
  priceUom: '',
  incoterms: '',
  paymentTerms: '',
  securityDeposit: true,
  renewalTerms: '',
  terminationTerms: '',
  maintenanceResponsibility: '',
  utilitiesResponsibility: '',
  propertyTaxResponsibility: '',
  escalationBasis: '',
  addOnCategories: '',
  markupBasis: '',
  isPriceNegotiable: true,
};

export default function ContractManufacturerProfile() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('1');
  const [facilityDetailsSaved, setFacilityDetailsSaved] = useState(false);
  const [productionDetailsSaved, setProductionDetailsSaved] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [facility, setFacility] = useState(defaultFacility);
  const [facilityDetailsList, setFacilityDetailsList] = useState(() => [createFacilityDetailsEntry()]);
  const [ownershipComplianceList, setOwnershipComplianceList] = useState(() => [createOwnershipComplianceEntry()]);
  const [locationContextIndex, setLocationContextIndex] = useState(0);
  const [production, setProduction] = useState(defaultProduction);
  const [product, setProduct] = useState(defaultProduct);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [uploadingFacilityImages, setUploadingFacilityImages] = useState(false);
  const [uploadingFacilityImageEntry, setUploadingFacilityImageEntry] = useState(null);
  const [uploadingComplianceCertificates, setUploadingComplianceCertificates] = useState(false);
  const [uploadingComplianceEntry, setUploadingComplianceEntry] = useState(null);
  const [uploadingMajorClientLogo, setUploadingMajorClientLogo] = useState(false);
  const [savingFactoryDetails, setSavingFactoryDetails] = useState(false);
  const [savingProductionDetails, setSavingProductionDetails] = useState(false);
  const [uploadingSustainabilityDoc, setUploadingSustainabilityDoc] = useState(null);
  const [uploadingEthicalDoc, setUploadingEthicalDoc] = useState(null);
  const [uploadingEmergencyPlan, setUploadingEmergencyPlan] = useState(false);
  const [industryTypeOptions, setIndustryTypeOptions] = useState(INDUSTRY_TYPE_MAP);

  useEffect(() => {
    let cancelled = false;
    cmIndustryTypeService
      .listActive()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (items.length) {
          setIndustryTypeOptions(
            items.map((row) => ({ value: row.name, label: row.name })),
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const { countries: rawCountries, states: rawStates, cities: rawCities, updateListsForCountry, updateListsForState } = useLocationMaster();
  const {
    categoryData,
    loadingCategories,
    subCategories: hookSubCategories,
    loadingSubCategories: hookLoadingSubCategories,
    subSubCategories: hookSubSubCategories,
    loadingSubSubCategories: hookLoadingSubSubCategories,
    fetchSubCategories,
    fetchSubSubCategories,
    customizationTypesOptions,
    dimensionUnitOptions,
  } = useProductDetailsMaster();
  const { currencyOptions } = useCurrencyMaster();
  const countries = useMemo(
    () => rawCountries.map((c) => ({ value: c.id, label: c.label })),
    [rawCountries],
  );
  const states = useMemo(
    () => rawStates.map((s) => ({ value: s.id, label: s.label })),
    [rawStates],
  );
  const cities = useMemo(
    () => rawCities.map((c) => ({ value: c.id, label: c.label })),
    [rawCities],
  );
  const companyProfileDefaultsAppliedRef = useRef(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [uploadingProductFiles, setUploadingProductFiles] = useState(false);
  const [descModal, setDescModal] = useState({ visible: false, rowId: null });
  const [descText, setDescText] = useState('');
  const [descFiles, setDescFiles] = useState([]);
  const [colourOptionsList, setColourOptionsList] = useState([
    { id: 1, color: '#ED00BE', imageUrl: '' },
  ]);
  const [dimensionsList, setDimensionsList] = useState([
    { id: 1, height: '', length: '', width: '', weight: '', uom: '' },
  ]);
  const [leadTimeList, setLeadTimeList] = useState([
    { id: 1, days: '', min: '', max: '' },
  ]);
  const [faqList, setFaqList] = useState([
    { id: 1, question: '', answer: '' },
  ]);
  const [productList, setProductList] = useState([]);
  const [productFormCollapsed, setProductFormCollapsed] = useState(true);
  const [editingProductId, setEditingProductId] = useState(null);
  const [customizationRows, setCustomizationRows] = useState([
    { id: 'logo', name: 'LOGO', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'color', name: 'Color', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'labels', name: 'Labels', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'packing', name: 'Packing', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'graphic', name: 'Graphic', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'sketch', name: 'Sketch', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    { id: 'sample_based', name: 'Sample-based', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
  ]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const entry = facilityDetailsList[locationContextIndex];
    if (entry?.country) updateListsForCountry(entry.country);
  }, [facilityDetailsList, locationContextIndex]);

  useEffect(() => {
    const entry = facilityDetailsList[locationContextIndex];
    if (entry?.country && entry?.state) updateListsForState(entry.country, entry.state);
  }, [facilityDetailsList, locationContextIndex]);

  // Auto-populate facility from seller/company profile once on first load (only fill blanks).
  // Do not re-run when the user clears a field — that was causing erased values to snap back.
  useEffect(() => {
    if (loading || !companyProfile || companyProfileDefaultsAppliedRef.current) return;

    const addr = typeof companyProfile.address === 'object' && companyProfile.address ? companyProfile.address : {};
    const contact = typeof companyProfile.contact_details === 'object' && companyProfile.contact_details ? companyProfile.contact_details : {};
    const countryVal = addr.country != null && addr.country !== '' ? addr.country : null;
    const needsCountryList = Boolean(countryVal) && countries.length === 0;
    const needsStateList = Boolean(addr.state) && states.length === 0;
    const needsCityList = Boolean(addr.city) && cities.length === 0;
    if (needsCountryList || needsStateList || needsCityList) return;

    setFacility((prev) => {
      const next = { ...prev };
      let changed = false;
      if (!String(next.companyName || '').trim()) { next.companyName = companyProfile.company_name || ''; changed = true; }
      if (!String(next.facilityName || '').trim()) { next.facilityName = companyProfile.company_name || ''; changed = true; }
      if (!String(next.address || '').trim()) {
        next.address = (typeof addr === 'string' ? addr : (addr.address || '')) || '';
        changed = true;
      }
      if (!String(next.pincode || '').trim()) {
        next.pincode = (typeof addr === 'string' ? '' : (addr.pincode || '')) || '';
        changed = true;
      }
      if (!String(next.contactName || '').trim()) { next.contactName = contact.contact_name || ''; changed = true; }
      if (!String(next.contactEmail || '').trim()) { next.contactEmail = contact.contact_email || companyProfile.user_email || ''; changed = true; }
      if (!String(next.contactPhone || '').trim()) { next.contactPhone = contact.contact_number || companyProfile.user_phone || ''; changed = true; }
      if (!String(next.companyRegNo || '').trim()) { next.companyRegNo = companyProfile.registration_number || ''; changed = true; }
      if (!String(next.taxId || '').trim()) { next.taxId = companyProfile.tax_number || ''; changed = true; }
      if (!next.country && countryVal && countries.length > 0) {
        const cOpt = countries.find((c) => String(c.value) === String(countryVal) || (c.label && String(c.label).toLowerCase() === String(countryVal).toLowerCase()));
        if (cOpt) { next.country = cOpt.value; changed = true; }
      }
      if (!next.state && addr.state != null && addr.state !== '' && states.length > 0) {
        const sOpt = states.find((s) => String(s.value) === String(addr.state) || (s.label && String(s.label).toLowerCase() === String(addr.state).toLowerCase()));
        if (sOpt) { next.state = sOpt.value; changed = true; }
      }
      if (!next.city && addr.city != null && addr.city !== '' && cities.length > 0) {
        const cityOpt = cities.find((c) => String(c.value) === String(addr.city) || (c.label && String(c.label).toLowerCase() === String(addr.city).toLowerCase()));
        if (cityOpt) { next.city = cityOpt.value; changed = true; }
      }
      if (changed) {
        setFacilityDetailsList((fdPrev) => {
          if (!fdPrev.length) return [createFacilityDetailsEntry(next)];
          return fdPrev.map((row, i) => (i === 0 ? createFacilityDetailsEntry({ ...row, ...next }) : row));
        });
      }
      return changed ? next : prev;
    });

    companyProfileDefaultsAppliedRef.current = true;
  }, [loading, companyProfile, countries, states, cities]);

  const loadData = async () => {
    setLoading(true);
    try {
      const myProfileRes = await CommonService.get('/company/my-profile');
      const cid = myProfileRes?.data?.company_id ?? myProfileRes?.data?.id ?? myProfileRes?.company_id;
      if (cid) {
        setCompanyId(cid);
        setCompanyProfile(myProfileRes?.data || myProfileRes);
      }

      const cmRes = await CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_PROFILE);
      const profile = cmRes?.data ?? cmRes;

      // Fetch seller factory profile for Supply Chain Logistics (port, airport, highway) when we have company id
      let firstFactory = null;
      if (cid) {
        try {
          const factoryRes = await CommonService.get(`/profile/company/${cid}/factories`);
          const factories = factoryRes?.data != null ? (Array.isArray(factoryRes.data) ? factoryRes.data : [factoryRes.data]) : [];
          if (factories.length > 0) firstFactory = factories[0];
        } catch (_) { /* ignore */ }
      }

      if (profile) {
        if (profile.facility_details && typeof profile.facility_details === 'object') {
          const fd = { ...profile.facility_details };
          const rawImgs = fd.facilityImages ?? fd.facility_images ?? fd.facilityImageUrls ?? fd.facility_image_urls;
          let facilityImagesNorm = [];
          if (Array.isArray(rawImgs)) {
            facilityImagesNorm = rawImgs
              .map((x) => {
                if (typeof x === 'string' && x.trim()) return x.trim();
                if (x && typeof x === 'object') {
                  return (
                    x.url
                    || x.accessPath
                    || x.filePath
                    || x.src
                    || x.path
                    || x.imageUrl
                    || x.image_url
                    || ''
                  ).trim();
                }
                return '';
              })
              .filter(Boolean);
          } else if (typeof rawImgs === 'string' && rawImgs.trim()) {
            facilityImagesNorm = [rawImgs.trim()];
          }
          fd.facilityImages = facilityImagesNorm;
          fd.complianceCertificates = fd.complianceCertificates ?? fd.compliance_certificates ?? [];
          if (!Array.isArray(fd.complianceCertificates)) fd.complianceCertificates = [];
          const toArr = (v, alt) => (Array.isArray(v) ? v : Array.isArray(alt) ? alt : v ? [v] : []);
          fd.insuranceCoverage = toArr(fd.insuranceCoverage, fd.insurance_coverage);
          fd.regulatoryApprovals = toArr(fd.regulatoryApprovals, fd.regulatory_approvals);
          fd.complianceCertificate = toArr(fd.complianceCertificate, fd.compliance_certificate);
          fd.sanctionedPowerLoadUnit = fd.sanctionedPowerLoadUnit ?? fd.sanctioned_power_load_unit ?? 'kva';
          fd.powerSupplyCapacityUnit = fd.powerSupplyCapacityUnit ?? fd.power_supply_capacity_unit ?? 'kva';
          fd.waterSupplyCapacityUnit = fd.waterSupplyCapacityUnit ?? fd.water_supply_capacity_unit ?? 'hour';
          fd.wasteWaterTreatmentDetails = fd.wasteWaterTreatmentDetails ?? fd.waste_water_treatment_details ?? '';
          fd.wasteManagementSystemDetails = fd.wasteManagementSystemDetails ?? fd.waste_management_system_details ?? '';
          fd.rawMaterialWarehouseUnit = fd.rawMaterialWarehouseUnit ?? fd.raw_material_warehouse_unit ?? 'sq_ft';
          fd.coldStorageUnit = fd.coldStorageUnit ?? fd.cold_storage_unit ?? 'mt';
          fd.finishedGoodsStorageUnit = fd.finishedGoodsStorageUnit ?? fd.finished_goods_storage_unit ?? 'sq_ft';
          fd.fireSafetyEquipments = toArr(fd.fireSafetyEquipments, fd.fire_safety_equipments);
          fd.portDistanceUnit = fd.portDistanceUnit ?? fd.port_distance_unit ?? 'km';
          fd.airportDistanceUnit = fd.airportDistanceUnit ?? fd.airport_distance_unit ?? 'km';
          fd.highwayDistanceUnit = fd.highwayDistanceUnit ?? fd.highway_distance_unit ?? 'km';
          fd.majorClientLogo = fd.majorClientLogo ?? fd.major_client_logo ?? '';
          const industryTypes = Array.isArray(fd.industryTypes)
            ? fd.industryTypes
            : fd.industryType
              ? (typeof fd.industryType === 'string' ? [fd.industryType] : [])
              : [];
          const normalizedFd = normalizeStorageFacilityFields(
            normalizeFacilityAreaFields({ ...fd, industryTypes }),
          );
          setFacility((prev) => ({ ...defaultFacility, ...prev, ...normalizedFd }));
          const rawFdList = fd.facilityDetailsList ?? fd.facility_details_list;
          const rawOcList = fd.ownershipComplianceList ?? fd.ownership_compliance_list;
          setFacilityDetailsList(
            Array.isArray(rawFdList) && rawFdList.length > 0
              ? rawFdList.map((row) => createFacilityDetailsEntry({ ...normalizedFd, ...row }))
              : [createFacilityDetailsEntry(normalizedFd)],
          );
          setOwnershipComplianceList(
            Array.isArray(rawOcList) && rawOcList.length > 0
              ? rawOcList.map((row) => createOwnershipComplianceEntry({ ...normalizedFd, ...row }))
              : [createOwnershipComplianceEntry(normalizedFd)],
          );
          setFacilityDetailsSaved(true);
        }
        if (profile.production_details && typeof profile.production_details === 'object') {
          const pd = { ...profile.production_details };
          const toArr = (v, alt) => (Array.isArray(v) ? v : Array.isArray(alt) ? alt : v ? [v] : []);
          pd.processes = toArr(pd.processes, pd.processes_list);
          pd.label = toArr(pd.label, pd.labels);
          pd.technicalExpertise = toArr(pd.technicalExpertise, pd.technical_expertise);
          pd.machineCapacityValue = pd.machineCapacityValue ?? pd.machine_capacity_value ?? '';
          pd.machineCapacityPeriod = pd.machineCapacityPeriod ?? pd.machine_capacity_period ?? '';
          pd.futureExpansionCapacityValue = pd.futureExpansionCapacityValue ?? pd.future_expansion_capacity_value ?? '';
          pd.futureExpansionCapacityUnit = pd.futureExpansionCapacityUnit ?? pd.future_expansion_capacity_unit ?? '';
          pd.capabilitiesList = Array.isArray(pd.capabilitiesList) ? pd.capabilitiesList : Array.isArray(pd.capabilities_list) ? pd.capabilities_list : [];
          if (pd.capabilitiesList.length === 0) pd.capabilitiesList = [{ ...defaultCapabilityEntry }];
          pd.capabilitiesList = pd.capabilitiesList.map((c) => {
            const toArr = (v, alt) => (Array.isArray(v) ? v : Array.isArray(alt) ? alt : v ? [v] : []);
            const processTypeValues = [
              ...toArr(c.manufacturingProcessType, c.manufacturing_process_type),
              ...toArr(c.manufacturingProcessesType, c.manufacturing_processes_type),
            ].filter((v, i, arr) => arr.indexOf(v) === i);
            return {
              ...defaultCapabilityEntry,
              ...c,
              manufacturingProcessType: processTypeValues,
              processes: toArr(c.processes, c.processes_list),
              label: toArr(c.label, c.labels),
              technicalExpertise: toArr(c.technicalExpertise, c.technical_expertise),
              operationalStatus: toArr(c.operationalStatus, c.operational_status),
            };
          });
          pd.supplierCertifications = Array.isArray(pd.supplierCertifications) ? pd.supplierCertifications : Array.isArray(pd.supplier_certifications) ? pd.supplier_certifications : [];
          pd.traceabilityReports = Array.isArray(pd.traceabilityReports) ? pd.traceabilityReports : Array.isArray(pd.traceability_reports) ? pd.traceability_reports : [];
          pd.transparencyDocumentation = Array.isArray(pd.transparencyDocumentation) ? pd.transparencyDocumentation : Array.isArray(pd.transparency_documentation) ? pd.transparency_documentation : [];
          pd.sustainabilityCertificate = toArr(pd.sustainabilityCertificate, pd.sustainability_certificate);
          pd.responsibleSourcing = toArr(pd.responsibleSourcing, pd.responsible_sourcing);
          pd.socialLaborPractices = toArr(pd.socialLaborPractices, pd.social_labor_practices);
          pd.environmentalImpact = toArr(pd.environmentalImpact, pd.environmental_impact);
          pd.codeOfConductDocuments = Array.isArray(pd.codeOfConductDocuments) ? pd.codeOfConductDocuments : Array.isArray(pd.code_of_conduct_documents) ? pd.code_of_conduct_documents : [];
          pd.ethicalSourcingPolicyDocuments = Array.isArray(pd.ethicalSourcingPolicyDocuments) ? pd.ethicalSourcingPolicyDocuments : Array.isArray(pd.ethical_sourcing_policy_documents) ? pd.ethical_sourcing_policy_documents : [];
          pd.labourPractices = toArr(pd.labourPractices, pd.labour_practices);
          pd.auditCompliance = toArr(pd.auditCompliance, pd.audit_compliance);
          pd.qualityCertificationAvailable = toArr(pd.qualityCertificationAvailable, pd.quality_certification_available);
          pd.inspectionMethods = Array.isArray(pd.inspectionMethods) ? (pd.inspectionMethods[0] ?? '') : (pd.inspectionMethods ?? pd.inspection_methods ?? '');
          pd.testingFacilitiesDetails = pd.testingFacilitiesDetails ?? pd.testing_facilities_details ?? '';
          pd.emergencyPlanDocuments = Array.isArray(pd.emergencyPlanDocuments) ? pd.emergencyPlanDocuments : Array.isArray(pd.emergency_plan_documents) ? pd.emergency_plan_documents : [];
          pd.workerTrainingProgramDetails = pd.workerTrainingProgramDetails ?? pd.worker_training_program_details ?? '';
          setProduction((prev) => ({ ...defaultProduction, ...prev, ...pd }));
          setProductionDetailsSaved(true);
        }
        if (profile.product_details && typeof profile.product_details === 'object') {
          const pd = { ...profile.product_details };
          const toArr = (v) => (Array.isArray(v) ? v : typeof v === 'string' && v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
          pd.keywords = toArr(pd.keywords ?? pd.keywords);
          pd.productCategoryId = pd.productCategoryId ?? pd.category_id ?? '';
          pd.productSubCategoryId = pd.productSubCategoryId ?? pd.subcategory_id ?? '';
          pd.productSubSubCategoryId = pd.productSubSubCategoryId ?? pd.sub_subcategory_id ?? '';
          pd.mainProduct = !!pd.mainProduct || !!pd.main_product;
          pd.productCatalogueUrl = pd.productCatalogueUrl ?? pd.product_catalogue ?? pd.pd_catalogue_path ?? '';
          pd.productCompanyLogoUrl = pd.productCompanyLogoUrl ?? pd.product_company_logo ?? pd.company_logo ?? '';
          pd.productVideoUrl = pd.productVideoUrl ?? pd.product_video ?? pd.pd_video_path ?? '';
          pd.productionCapacityValue = pd.productionCapacityValue ?? pd.production_capacity_value ?? (typeof pd.productionCapacity === 'string' && pd.productionCapacity ? pd.productionCapacity.replace(/[^0-9.]/g, '') : '') ?? '';
          pd.productionCapacityUnit = pd.productionCapacityUnit ?? pd.production_capacity_unit ?? '';
          pd.productionCapacityPeriod = pd.productionCapacityPeriod ?? pd.production_capacity_period ?? '';
          pd.customizationAvailable = !!pd.customizationAvailable || !!pd.customization_available;
          pd.minimumOrderQuantity = pd.minimumOrderQuantity ?? pd.moq_min ?? pd.moq?.min_quantity ?? (typeof pd.moq === 'number' ? pd.moq : '') ?? '';
          pd.moqUom = pd.moqUom ?? pd.moq_uom ?? pd.moq?.unit_of_measurement ?? '';
          pd.moqPriceNotax = pd.moqPriceNotax ?? pd.moq_price_notax ?? pd.moq?.price_without_tax ?? '';
          pd.moqTax = pd.moqTax ?? pd.moq_tax ?? pd.moq?.tax_percentage ?? '';
          pd.moqPriceTax = pd.moqPriceTax ?? pd.moq_price_tax ?? pd.moq?.price_with_tax ?? '';
          pd.moqCurrency = pd.moqCurrency ?? pd.moq_currency ?? pd.moq?.currency ?? '';
          pd.priceCurrency = pd.priceCurrency ?? pd.price_currency ?? '';
          pd.renewalTerms = pd.renewalTerms ?? pd.renewal_terms ?? '';
          pd.terminationTerms = pd.terminationTerms ?? pd.termination_terms ?? '';
          pd.maintenanceResponsibility = pd.maintenanceResponsibility ?? pd.maintenance_responsibility ?? '';
          pd.utilitiesResponsibility = pd.utilitiesResponsibility ?? pd.utilities_responsibility ?? '';
          pd.propertyTaxResponsibility = pd.propertyTaxResponsibility ?? pd.property_tax_responsibility ?? '';
          pd.escalationBasis = pd.escalationBasis ?? pd.escalation_basis ?? '';
          pd.addOnCategories = pd.addOnCategories ?? pd.add_on_categories ?? '';
          pd.markupBasis = pd.markupBasis ?? pd.markup_basis ?? '';
          setProduct((prev) => ({ ...defaultProduct, ...prev, ...pd }));
          if (Array.isArray(pd.customizationRows) && pd.customizationRows.length > 0) {
            setCustomizationRows(pd.customizationRows);
          } else if (Array.isArray(pd.customization_rows) && pd.customization_rows.length > 0) {
            setCustomizationRows(pd.customization_rows);
          }
          if (Array.isArray(pd.colourOptionsList) && pd.colourOptionsList.length > 0) {
            setColourOptionsList(pd.colourOptionsList);
          } else if (Array.isArray(pd.colour_options) && pd.colour_options.length > 0) {
            setColourOptionsList(pd.colour_options.map((opt, idx) => ({
              id: opt.id ?? idx + 1,
              color: opt.color || opt.colour || '#ED00BE',
              imageUrl: opt.imageUrl || opt.image_url || '',
            })));
          }
          if (Array.isArray(pd.dimensionsList) && pd.dimensionsList.length > 0) {
            setDimensionsList(pd.dimensionsList);
          } else if (Array.isArray(pd.dimensions_list) && pd.dimensions_list.length > 0) {
            setDimensionsList(pd.dimensions_list.map((d, idx) => ({
              id: d.id ?? idx + 1,
              height: d.height ?? '',
              length: d.length ?? '',
              width: d.width ?? '',
              weight: d.weight ?? '',
              uom: d.uom ?? '',
            })));
          }
          const leadTimeData = pd.lead_time_list || (pd.lead_time?.items && Array.isArray(pd.lead_time.items) ? pd.lead_time.items : null) || (Array.isArray(pd.lead_time) ? pd.lead_time : null);
          if (leadTimeData && leadTimeData.length > 0) {
            setLeadTimeList(leadTimeData.map((lead, idx) => ({
              id: lead.id ?? idx + 1,
              days: lead.lead_time_days ?? lead.days ?? '',
              min: lead.min_quantity ?? lead.min ?? '',
              max: lead.max_quantity ?? lead.max ?? '',
            })));
          } else if (pd.lead_time && typeof pd.lead_time === 'object' && !Array.isArray(pd.lead_time) && (pd.lead_time.lead_time_days !== undefined || pd.lead_time.min_quantity !== undefined)) {
            setLeadTimeList([{
              id: 1,
              days: pd.lead_time.lead_time_days ?? pd.lead_time.days ?? '',
              min: pd.lead_time.min_quantity ?? pd.lead_time.min ?? '',
              max: pd.lead_time.max_quantity ?? pd.lead_time.max ?? '',
            }]);
          }
          if (Array.isArray(pd.faqList) && pd.faqList.length > 0) {
            setFaqList(pd.faqList.map((faq, idx) => ({
              id: faq.id ?? idx + 1,
              question: faq.question ?? faq.q ?? '',
              answer: faq.answer ?? faq.a ?? '',
            })));
          } else if (Array.isArray(pd.faq) && pd.faq.length > 0) {
            setFaqList(pd.faq.map((faq, idx) => ({
              id: faq.id ?? idx + 1,
              question: faq.question ?? faq.q ?? '',
              answer: faq.answer ?? faq.a ?? '',
            })));
          } else if (Array.isArray(pd.faq_list) && pd.faq_list.length > 0) {
            setFaqList(pd.faq_list.map((faq, idx) => ({
              id: faq.id ?? idx + 1,
              question: faq.question ?? faq.q ?? '',
              answer: faq.answer ?? faq.a ?? '',
            })));
          }
          if (Array.isArray(profile.product_details.products) && profile.product_details.products.length > 0) {
            setProductList(profile.product_details.products);
            setProductFormCollapsed(true);
            setEditingProductId(null);
          } else {
            const cr = Array.isArray(pd.customizationRows) ? pd.customizationRows : (Array.isArray(pd.customization_rows) ? pd.customization_rows : []);
            const col = Array.isArray(pd.colourOptionsList) ? pd.colourOptionsList : (Array.isArray(pd.colour_options) ? pd.colour_options.map((opt, idx) => ({ id: opt.id ?? idx + 1, color: opt.color || opt.colour || '#ED00BE', imageUrl: opt.imageUrl || opt.image_url || '' })) : []);
            const dl = Array.isArray(pd.dimensionsList) ? pd.dimensionsList : (Array.isArray(pd.dimensions_list) ? pd.dimensions_list.map((d, idx) => ({ id: d.id ?? idx + 1, height: d.height ?? '', length: d.length ?? '', width: d.width ?? '', weight: d.weight ?? '', uom: d.uom ?? '' })) : []);
            const lt = Array.isArray(pd.lead_time_list) ? pd.lead_time_list.map((lead, idx) => ({ id: lead.id ?? idx + 1, days: lead.lead_time_days ?? lead.days ?? '', min: lead.min_quantity ?? lead.min ?? '', max: lead.max_quantity ?? lead.max ?? '' })) : (pd.lead_time?.items && Array.isArray(pd.lead_time.items) ? pd.lead_time.items.map((lead, idx) => ({ id: lead.id ?? idx + 1, days: lead.lead_time_days ?? lead.days ?? '', min: lead.min_quantity ?? lead.min ?? '', max: lead.max_quantity ?? lead.max ?? '' })) : []);
            const fl = Array.isArray(pd.faqList) ? pd.faqList : (Array.isArray(pd.faq) ? pd.faq.map((faq, idx) => ({ id: faq.id ?? idx + 1, question: faq.question ?? faq.q ?? '', answer: faq.answer ?? faq.a ?? '' })) : (Array.isArray(pd.faq_list) ? pd.faq_list.map((faq, idx) => ({ id: faq.id ?? idx + 1, question: faq.question ?? faq.q ?? '', answer: faq.answer ?? faq.a ?? '' })) : []));
            const entry = { id: pd.id ?? 1, ...pd, customizationRows: cr.length ? cr : undefined, colourOptionsList: col.length ? col : undefined, dimensionsList: dl.length ? dl : undefined, leadTimeList: lt.length ? lt : undefined, faqList: fl.length ? fl : undefined };
            setProductList([entry]);
            setProductFormCollapsed(false);
            setEditingProductId(entry.id);
          }
        }
      }

      if (cid && myProfileRes?.data && !profile) {
        const cp = myProfileRes.data;
        const addr = typeof cp.address === 'object' && cp.address ? cp.address : {};
        const contact = typeof cp.contact_details === 'object' && cp.contact_details ? cp.contact_details : {};
        setFacility((prev) => ({
          ...prev,
          companyName: cp.company_name || prev.companyName,
          facilityName: cp.company_name || prev.facilityName,
          companyRegNo: cp.registration_number || prev.companyRegNo,
          taxId: cp.tax_number || prev.taxId,
          address: (typeof addr === 'object' ? addr.address : cp.address) || prev.address,
          pincode: (typeof addr === 'object' ? addr.pincode : cp.pincode) || prev.pincode,
          contactName: contact.contact_name || prev.contactName,
          contactEmail: contact.contact_email || cp.user_email || prev.contactEmail,
          contactPhone: contact.contact_number || cp.user_phone || prev.contactPhone,
          country: cp.country_id ?? addr.country ?? prev.country,
          state: cp.state_id ?? addr.state ?? prev.state,
          city: cp.city_id ?? addr.city ?? prev.city,
        }));
      }

      // Auto-populate factory details from saved seller/company profile for any empty facility fields (runs on every load)
      if (myProfileRes?.data) {
        const cp = myProfileRes.data;
        const addr = typeof cp.address === 'object' && cp.address ? cp.address : {};
        const contact = typeof cp.contact_details === 'object' && cp.contact_details ? cp.contact_details : {};
        setFacility((prev) => {
          const next = { ...prev };
          let changed = false;
          if (!String(next.companyName || '').trim()) { next.companyName = cp.company_name || ''; changed = true; }
          if (!String(next.facilityName || '').trim()) { next.facilityName = cp.company_name || ''; changed = true; }
          if (!String(next.address || '').trim()) {
            next.address = (typeof addr === 'string' ? addr : (addr.address || '')) || '';
            changed = true;
          }
          if (!String(next.pincode || '').trim()) {
            next.pincode = (typeof addr === 'string' ? '' : (addr.pincode || '')) || '';
            changed = true;
          }
          if (!String(next.contactName || '').trim()) { next.contactName = contact.contact_name || ''; changed = true; }
          if (!String(next.contactEmail || '').trim()) { next.contactEmail = contact.contact_email || cp.user_email || ''; changed = true; }
          if (!String(next.contactPhone || '').trim()) { next.contactPhone = contact.contact_number || cp.user_phone || ''; changed = true; }
          if (!String(next.contactDesignation || '').trim()) { next.contactDesignation = cp.designation || ''; changed = true; }
          if (!String(next.companyRegNo || '').trim()) { next.companyRegNo = cp.registration_number || ''; changed = true; }
          if (!String(next.taxId || '').trim()) { next.taxId = cp.tax_number || ''; changed = true; }
          const countryVal = cp.country_id ?? (addr.country != null && addr.country !== '' ? addr.country : null);
          if (!next.country && countryVal !== undefined && countryVal !== null && countryVal !== '') {
            next.country = String(countryVal);
            changed = true;
          }
          const stateVal = cp.state_id ?? (addr.state != null && addr.state !== '' ? addr.state : null);
          if (!next.state && stateVal !== undefined && stateVal !== null && stateVal !== '') {
            next.state = String(stateVal);
            changed = true;
          }
          const cityVal = cp.city_id ?? (addr.city != null && addr.city !== '' ? addr.city : null);
          if (!next.city && cityVal !== undefined && cityVal !== null && cityVal !== '') {
            next.city = String(cityVal);
            changed = true;
          }
          return changed ? next : prev;
        });
      }

      // Auto-populate Supply Chain Logistics from saved seller factory profile (port, airport, highway) when fields are empty
      if (firstFactory) {
        setFacility((prev) => {
          const next = { ...prev };
          let changed = false;
          if (!String(next.portName || '').trim()) {
            next.portName = firstFactory.nearest_port || '';
            if (next.portName) changed = true;
          }
          if (!String(next.portDistanceKm || '').trim() && (firstFactory.distance_port != null && firstFactory.distance_port !== '')) {
            next.portDistanceKm = String(firstFactory.distance_port);
            changed = true;
          }
          if (!String(next.airportName || '').trim()) {
            next.airportName = firstFactory.nearest_airport || '';
            if (next.airportName) changed = true;
          }
          if (!String(next.airportDistanceKm || '').trim() && (firstFactory.distance_airport != null && firstFactory.distance_airport !== '')) {
            next.airportDistanceKm = String(firstFactory.distance_airport);
            changed = true;
          }
          if (!String(next.highwayName || '').trim()) {
            next.highwayName = firstFactory.highway_name || '';
            if (next.highwayName) changed = true;
          }
          if (!String(next.highwayDistanceKm || '').trim() && (firstFactory.highway_distance != null && firstFactory.highway_distance !== '')) {
            next.highwayDistanceKm = String(firstFactory.highway_distance);
            changed = true;
          }
          return changed ? next : prev;
        });
      }
    } catch (err) {
      if (err?.response?.status !== 404) {
        console.error('Error loading contract manufacturer profile:', err);
        toast.error('Failed to load profile');
      }
      const myProfileRes = await CommonService.get('/company/my-profile').catch(() => null);
      const cid = myProfileRes?.data?.company_id ?? myProfileRes?.data?.id;
      if (cid) setCompanyId(cid);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!product.productCategoryId) {
      fetchSubCategories(null);
      return;
    }
    fetchSubCategories(product.productCategoryId);
  }, [product.productCategoryId]);

  useEffect(() => {
    if (!product.productSubCategoryId) {
      fetchSubSubCategories(null);
      return;
    }
    fetchSubSubCategories(product.productSubCategoryId);
  }, [product.productSubCategoryId]);

  const updateFacility = (field, value) => setFacility((prev) => ({ ...prev, [field]: value }));
  const updateProduction = (field, value) => setProduction((prev) => ({ ...prev, [field]: value }));
  const updateProduct = (field, value) => setProduct((prev) => ({ ...prev, [field]: value }));

  const syncFacilityDetailsEntryToFacility = (entry) => {
    setFacility((prev) => {
      const next = { ...prev };
      FACILITY_DETAILS_ENTRY_FIELDS.forEach((key) => {
        next[key] = entry[key];
      });
      return next;
    });
  };

  const updateFacilityDetailsEntry = (index, field, value) => {
    setFacilityDetailsList((prev) => {
      const next = prev.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };
        if (field === 'country') {
          updated.state = '';
          updated.city = '';
          const countryOpt = countries.find((c) => String(c.value) === String(value));
          updated.countryName = countryOpt?.label || '';
          updated.stateName = '';
          updated.cityName = '';
        }
        if (field === 'state') {
          updated.city = '';
          const stateOpt = states.find((s) => String(s.value) === String(value));
          updated.stateName = stateOpt?.label || '';
          updated.cityName = '';
        }
        if (field === 'city') {
          const cityOpt = cities.find((c) => String(c.value) === String(value));
          updated.cityName = cityOpt?.label || '';
        }
        return updated;
      });
      if (index === 0) syncFacilityDetailsEntryToFacility(next[0]);
      return next;
    });
  };

  const addFacilityDetailsEntry = () => {
    setFacilityDetailsList((prev) => [...prev, createFacilityDetailsEntry()]);
  };

  const removeFacilityDetailsEntry = (index) => {
    setFacilityDetailsList((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      if (index === 0) syncFacilityDetailsEntryToFacility(next[0]);
      return next;
    });
    setLocationContextIndex((prev) => {
      if (prev > index) return prev - 1;
      if (prev === index) return Math.max(0, index - 1);
      return prev;
    });
  };

  const updateOwnershipComplianceEntry = (index, field, value) => {
    setOwnershipComplianceList((prev) => {
      const next = prev.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      if (index === 0) {
        setFacility((fprev) => ({ ...fprev, [field]: value }));
      }
      return next;
    });
  };

  const addOwnershipComplianceEntry = () => {
    setOwnershipComplianceList((prev) => [...prev, createOwnershipComplianceEntry()]);
  };

  const removeOwnershipComplianceEntry = (index) => {
    setOwnershipComplianceList((prev) => {
      if (prev.length <= 1) return prev;
      const next = prev.filter((_, i) => i !== index);
      if (index === 0) {
        const oc0 = next[0];
        setFacility((fprev) => {
          const updated = { ...fprev };
          OWNERSHIP_COMPLIANCE_ENTRY_FIELDS.forEach((key) => {
            updated[key] = oc0[key];
          });
          return updated;
        });
      }
      return next;
    });
  };

  const MAX_FACILITY_IMAGES = 5;
  const uploadFacilityImage = async (e, entryIndex = 0) => {
    const files = e.target.files;
    if (!files?.length) return;
    const entry = facilityDetailsList[entryIndex] || facilityDetailsList[0] || {};
    const current = entry.facilityImages?.length ?? 0;
    if (current >= MAX_FACILITY_IMAGES) {
      toast.info(`Maximum ${MAX_FACILITY_IMAGES} facility images allowed.`);
      e.target.value = '';
      return;
    }
    setUploadingFacilityImages(true);
    setUploadingFacilityImageEntry(entryIndex);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'factory');
      formData.append('companyName', entry.companyName || facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'facility_image');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setFacilityDetailsList((prev) => prev.map((row, i) => {
          if (i !== entryIndex) return row;
          const list = row.facilityImages || [];
          if (list.length >= MAX_FACILITY_IMAGES) return row;
          return { ...row, facilityImages: [...list, url] };
        }));
        if (entryIndex === 0) {
          setFacility((prev) => {
            const list = prev.facilityImages || [];
            if (list.length >= MAX_FACILITY_IMAGES) return prev;
            return { ...prev, facilityImages: [...list, url] };
          });
        }
        toast.success('Facility image uploaded');
      }
    } catch (err) {
      console.error('Facility image upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingFacilityImages(false);
      setUploadingFacilityImageEntry(null);
      e.target.value = '';
    }
  };

  const removeFacilityImage = (imgIndex, entryIndex = 0) => {
    setFacilityDetailsList((prev) => prev.map((row, i) => {
      if (i !== entryIndex) return row;
      return { ...row, facilityImages: (row.facilityImages || []).filter((_, j) => j !== imgIndex) };
    }));
    if (entryIndex === 0) {
      setFacility((prev) => ({
        ...prev,
        facilityImages: (prev.facilityImages || []).filter((_, i) => i !== imgIndex),
      }));
    }
  };

  const MAX_COMPLIANCE_CERTIFICATES = 5;
  const uploadComplianceCertificate = async (e, entryIndex = 0) => {
    const files = e.target.files;
    if (!files?.length) return;
    const ocEntry = ownershipComplianceList[entryIndex] || ownershipComplianceList[0] || {};
    const list = ocEntry.complianceCertificates ?? [];
    if (list.length >= MAX_COMPLIANCE_CERTIFICATES) {
      toast.info(`Maximum ${MAX_COMPLIANCE_CERTIFICATES} certificates allowed.`);
      e.target.value = '';
      return;
    }
    setUploadingComplianceCertificates(true);
    setUploadingComplianceEntry(entryIndex);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'company');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'compliance_certificate');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setOwnershipComplianceList((prev) => prev.map((row, i) => {
          if (i !== entryIndex) return row;
          const certs = row.complianceCertificates || [];
          if (certs.length >= MAX_COMPLIANCE_CERTIFICATES) return row;
          return { ...row, complianceCertificates: [...certs, url] };
        }));
        if (entryIndex === 0) {
          setFacility((prev) => {
            const certs = prev.complianceCertificates || [];
            if (certs.length >= MAX_COMPLIANCE_CERTIFICATES) return prev;
            return { ...prev, complianceCertificates: [...certs, url] };
          });
        }
        toast.success('Certificate uploaded');
      }
    } catch (err) {
      console.error('Certificate upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingComplianceCertificates(false);
      setUploadingComplianceEntry(null);
      e.target.value = '';
    }
  };
  const removeComplianceCertificate = (certIndex, entryIndex = 0) => {
    setOwnershipComplianceList((prev) => prev.map((row, i) => {
      if (i !== entryIndex) return row;
      return { ...row, complianceCertificates: (row.complianceCertificates || []).filter((_, j) => j !== certIndex) };
    }));
    if (entryIndex === 0) {
      setFacility((prev) => ({
        ...prev,
        complianceCertificates: (prev.complianceCertificates || []).filter((_, i) => i !== certIndex),
      }));
    }
  };

  const uploadSustainabilityDoc = (docType) => async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingSustainabilityDoc(docType);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'company');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      const fileTypeMap = { supplier: 'supplier_certification', traceability: 'traceability_report', transparency: 'transparency_document' };
      formData.append('fileType', fileTypeMap[docType] || 'sustainability_doc');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduction((prev) => {
          const key = docType === 'supplier' ? 'supplierCertifications' : docType === 'traceability' ? 'traceabilityReports' : 'transparencyDocumentation';
          const list = prev[key] || [];
          return { ...prev, [key]: [...list, url] };
        });
        toast.success('Document uploaded');
      }
    } catch (err) {
      console.error('Sustainability doc upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingSustainabilityDoc(null);
      e.target.value = '';
    }
  };
  const removeSustainabilityDoc = (docType, index) => {
    setProduction((prev) => {
      const key = docType === 'supplier' ? 'supplierCertifications' : docType === 'traceability' ? 'traceabilityReports' : 'transparencyDocumentation';
      const list = (prev[key] || []).filter((_, i) => i !== index);
      return { ...prev, [key]: list };
    });
  };

  const uploadEthicalDoc = (docType) => async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingEthicalDoc(docType);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'company');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', docType === 'codeOfConduct' ? 'code_of_conduct_doc' : 'ethical_sourcing_policy_doc');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduction((prev) => {
          const key = docType === 'codeOfConduct' ? 'codeOfConductDocuments' : 'ethicalSourcingPolicyDocuments';
          const list = prev[key] || [];
          return { ...prev, [key]: [...list, url] };
        });
        toast.success('Document uploaded');
      }
    } catch (err) {
      console.error('Ethical doc upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingEthicalDoc(null);
      e.target.value = '';
    }
  };
  const removeEthicalDoc = (docType, index) => {
    setProduction((prev) => {
      const key = docType === 'codeOfConduct' ? 'codeOfConductDocuments' : 'ethicalSourcingPolicyDocuments';
      const list = (prev[key] || []).filter((_, i) => i !== index);
      return { ...prev, [key]: list };
    });
  };

  const uploadEmergencyPlanPdf = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingEmergencyPlan(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'company');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'emergency_plan_pdf');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduction((prev) => ({
          ...prev,
          emergencyPlanDocuments: [...(prev.emergencyPlanDocuments || []), url],
        }));
        toast.success('Emergency plan PDF uploaded');
      }
    } catch (err) {
      console.error('Emergency plan upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingEmergencyPlan(false);
      e.target.value = '';
    }
  };
  const removeEmergencyPlanDoc = (index) => {
    setProduction((prev) => ({
      ...prev,
      emergencyPlanDocuments: (prev.emergencyPlanDocuments || []).filter((_, i) => i !== index),
    }));
  };

  const uploadMajorClientLogo = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingMajorClientLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('category', 'company');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'major_client_logo');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setFacility((prev) => ({ ...prev, majorClientLogo: url }));
        toast.success('Major client logo uploaded');
      }
    } catch (err) {
      console.error('Major client logo upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingMajorClientLogo(false);
      e.target.value = '';
    }
  };
  const removeMajorClientLogo = () => setFacility((prev) => ({ ...prev, majorClientLogo: '' }));

  const toggleIndustryType = (value) => {
    setFacility((prev) => {
      const arr = Array.isArray(prev.industryTypes) ? prev.industryTypes : [];
      const has = arr.includes(value);
      return { ...prev, industryTypes: has ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const getFacilityValidationErrors = () => {
    const fd0 = facilityDetailsList[0] || facility;
    const oc0 = ownershipComplianceList[0] || facility;
    const getVal = (f) => {
      if (FACILITY_DETAILS_ENTRY_FIELDS.includes(f)) return fd0[f];
      if (OWNERSHIP_COMPLIANCE_ENTRY_FIELDS.includes(f)) return oc0[f];
      return facility[f];
    };
    const required = ['companyName', 'facilityName', 'companyRegNo', 'contractManufacturingType', 'facilityType', 'factoryLicenceNumber', 'address', 'country', 'state', 'city', 'pincode', 'contactName', 'ownershipType', 'typeOfConstruction', 'totalFacilityArea', 'primaryPowerSource', 'rawMaterialWarehouse', 'annualTurnoverBracket', 'exportDomesticSplit'];
    const missing = required.filter((f) => !String(getVal(f) || '').trim());
    const industryTypes = Array.isArray(fd0.industryTypes) ? fd0.industryTypes : [];
    const industryTypesEmpty = industryTypes.length === 0;
    return { missing, industryTypesEmpty };
  };

  const isFacilityComplete = () => {
    const { missing, industryTypesEmpty } = getFacilityValidationErrors();
    return missing.length === 0 && !industryTypesEmpty;
  };

  const validateFacility = () => {
    const { missing, industryTypesEmpty } = getFacilityValidationErrors();
    if (missing.length) {
      toast.error('Please fill all required fields in Factory Details (marked with *)');
      return false;
    }
    if (industryTypesEmpty) {
      toast.error('Please select at least one Industry Type');
      return false;
    }
    return true;
  };

  const validateProduction = () => {
    const required = ['emissionReductionTarget', 'packagingSustainability', 'sustainabilityDescription', 'qualityCertificationAvailable', 'numberPermanentWorkers', 'numberContractWorkers', 'numberSeasonalWorkers', 'safetyIncidentLast12Months', 'emergencyPreparedness', 'emergencyPlanDocuments'];
    const missing = required.filter((f) => {
      const v = production[f];
      if (Array.isArray(v)) return v.length === 0;
      return !String(v || '').trim();
    });
    if (missing.includes('emergencyPlanDocuments')) {
      toast.error('Please upload at least one Emergency Plan document.');
      return false;
    }
    if (missing.includes('qualityCertificationAvailable')) {
      toast.error('Please add at least one Quality Certification.');
      return false;
    }
    const list = production.capabilitiesList || [];
    const atLeastOneValid = list.some((cap) => {
      const pt = (Array.isArray(cap.manufacturingProcessType) ? cap.manufacturingProcessType : []).length > 0;
      const pr = Array.isArray(cap.processes) ? cap.processes.length > 0 : false;
      const bs = String(cap.batchSize || '').trim();
      return pt && pr && bs;
    });
    if (!atLeastOneValid) {
      toast.error('Please fill at least one Manufacturing Capability (Process Type, Processes, and Batch Size).');
      return false;
    }
    if (missing.length) {
      toast.error('Please fill all required fields in Production Details (marked with *)');
      return false;
    }
    return true;
  };

  const validateProduct = () => {
    if (productFormCollapsed && productList && productList.length > 0) return true;
    if (productFormCollapsed && (!productList || productList.length === 0)) {
      toast.error('Please add at least one product (click Add Product and fill the form).');
      return false;
    }
    if (!String(product.productName || '').trim()) {
      toast.error('Please fill Product Name in Product Details');
      return false;
    }
    if (!String(product.brand || '').trim()) {
      toast.error('Please fill Brand in Product Details');
      return false;
    }
    if (!String(product.productCategoryId || '').trim()) {
      toast.error('Please select Product Category in Product Details');
      return false;
    }
    if (!String(product.productSubCategoryId || '').trim()) {
      toast.error('Please select Product Sub-category in Product Details');
      return false;
    }
    if (!String(product.productSubSubCategoryId || '').trim()) {
      toast.error('Please select Product Sub-Sub Category in Product Details');
      return false;
    }
    if (!String(product.productDescription || '').trim()) {
      toast.error('Please fill Product Description in Product Details');
      return false;
    }
    const kw = Array.isArray(product.keywords) ? product.keywords : [];
    if (kw.length === 0) {
      toast.error('Please add at least one Keyword in Product Details');
      return false;
    }
    if (!String(product.minimumOrderQuantity || '').trim()) {
      toast.error('Please fill MOQ (Minimum Order Quantity) in Product Details');
      return false;
    }
    return true;
  };

  const enrichFacilityDetailsEntryWithLocationLabels = (entry) => {
    const countryOpt = countries.find((c) => String(c.value) === String(entry.country));
    const stateOpt = states.find((s) => String(s.value) === String(entry.state));
    const cityOpt = cities.find((c) => String(c.value) === String(entry.city));
    return {
      ...entry,
      countryName: countryOpt?.label || entry.countryName || '',
      stateName: stateOpt?.label || entry.stateName || '',
      cityName: cityOpt?.label || entry.cityName || '',
    };
  };

  const buildFacilityPayloadWithLocationLabels = (fac, fdList = facilityDetailsList, ocList = ownershipComplianceList) => {
    const enrichedFdList = (fdList || []).map(enrichFacilityDetailsEntryWithLocationLabels);
    const firstFd = enrichedFdList[0] || {};
    const firstOc = (ocList || [])[0] || {};
    const merged = {
      ...fac,
      ...Object.fromEntries(FACILITY_DETAILS_ENTRY_FIELDS.map((k) => [k, firstFd[k] ?? fac[k]])),
      ...Object.fromEntries(OWNERSHIP_COMPLIANCE_ENTRY_FIELDS.map((k) => [k, firstOc[k] ?? fac[k]])),
      facilityDetailsList: enrichedFdList,
      ownershipComplianceList: ocList || [],
    };
    return normalizeStorageFacilityFields(
      normalizeFacilityAreaFields(merged),
    );
  };

  const handleSaveFactoryDetails = async () => {
    if (!companyId) {
      toast.error('Company profile not found. Please complete Seller Profile first.');
      navigate('/seller-profile');
      return;
    }

    setSavingFactoryDetails(true);
    try {
      const facilityToSave = buildFacilityPayloadWithLocationLabels(facility);
      const payload = { facility_details: facilityToSave };
      await CommonService.put(API_URLS.CONTRACT_MANUFACTURER.SAVE_PROFILE(companyId), payload);
      setFacilityDetailsSaved(true);
      if (isFacilityComplete()) {
        toast.success('Factory details saved successfully');
      } else {
        toast.success('Progress saved. Complete required fields (marked with *) before continuing.');
      }
      await loadData();
    } catch (err) {
      console.error('Save factory details error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save factory details');
    } finally {
      setSavingFactoryDetails(false);
    }
  };

  const handleFactoryDetailsNext = () => {
    if (!validateFacility()) return;
    setActiveTab('2');
  };

  const handleSaveProductionDetails = async () => {
    if (!companyId) {
      toast.error('Company profile not found. Please complete Seller Profile first.');
      navigate('/seller-profile');
      return;
    }
    if (!validateProduction()) return;

    setSavingProductionDetails(true);
    try {
      const payload = { production_details: production };
      await CommonService.put(API_URLS.CONTRACT_MANUFACTURER.SAVE_PROFILE(companyId), payload);
      toast.success('Production details saved successfully');
      setProductionDetailsSaved(true);
      await loadData();
    } catch (err) {
      console.error('Save production details error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save production details');
    } finally {
      setSavingProductionDetails(false);
    }
  };

  const handleSaveProductDetails = async () => {
    if (!companyId) {
      toast.error('Company profile not found. Please complete Seller Profile first.');
      navigate('/seller-profile');
      return;
    }
    if (!validateProduct()) return;

    setSaving(true);
    try {
      const validLeadTimes = (leadTimeList || [])
        .filter(lead => (lead.days !== undefined && lead.days !== '') || (lead.min !== undefined && lead.min !== '') || (lead.max !== undefined && lead.max !== ''))
        .map(lead => ({
          lead_time_days: lead.days === '' || lead.days == null ? undefined : parseInt(lead.days, 10),
          min_quantity: lead.min === '' || lead.min == null ? undefined : (isNaN(parseFloat(lead.min)) ? undefined : parseFloat(lead.min)),
          max_quantity: lead.max === '' || lead.max == null ? undefined : (isNaN(parseFloat(lead.max)) ? undefined : parseFloat(lead.max)),
        }))
        .filter(lead => lead.lead_time_days !== undefined || lead.min_quantity !== undefined || lead.max_quantity !== undefined);
      const validFAQs = (faqList || [])
        .filter(faq => faq.question && String(faq.question).trim() && faq.answer && String(faq.answer).trim())
        .map(faq => ({ question: String(faq.question).trim(), answer: String(faq.answer).trim() }));
      let listToSave = productList || [];
      if (!productFormCollapsed && (product.productName?.trim() || editingProductId)) {
        const snapshot = getCurrentProductSnapshot();
        listToSave = editingProductId
          ? (productList || []).map(p => p.id === editingProductId ? snapshot : p)
          : [...(productList || []), snapshot];
      }
      const productDetailsPayload = {
        ...product,
        customizationRows,
        colourOptionsList,
        dimensionsList,
        leadTimeList,
        lead_time_list: validLeadTimes.length > 0 ? validLeadTimes : undefined,
        faqList,
        faq: validFAQs.length > 0 ? validFAQs : undefined,
        faq_list: validFAQs.length > 0 ? validFAQs : undefined,
        products: listToSave,
      };
      const payload = { product_details: productDetailsPayload };
      await CommonService.put(API_URLS.CONTRACT_MANUFACTURER.SAVE_PROFILE(companyId), payload);
      toast.success('Product details saved successfully');
      setProductList(listToSave);
      setProductFormCollapsed(true);
      setEditingProductId(null);
      await loadData();
    } catch (err) {
      console.error('Save product details error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save product details');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!companyId) {
      toast.error('Company profile not found. Please complete Seller Profile first.');
      navigate('/seller-profile');
      return;
    }
    const facilityOk = validateFacility();
    const productionOk = validateProduction();
    const productOk = validateProduct();
    if (!facilityOk || !productionOk || !productOk) return;

    setSaving(true);
    try {
      const validLeadTimes = (leadTimeList || [])
        .filter(lead => (lead.days !== undefined && lead.days !== '') || (lead.min !== undefined && lead.min !== '') || (lead.max !== undefined && lead.max !== ''))
        .map(lead => ({
          lead_time_days: lead.days === '' || lead.days == null ? undefined : parseInt(lead.days, 10),
          min_quantity: lead.min === '' || lead.min == null ? undefined : (isNaN(parseFloat(lead.min)) ? undefined : parseFloat(lead.min)),
          max_quantity: lead.max === '' || lead.max == null ? undefined : (isNaN(parseFloat(lead.max)) ? undefined : parseFloat(lead.max)),
        }))
        .filter(lead => lead.lead_time_days !== undefined || lead.min_quantity !== undefined || lead.max_quantity !== undefined);
      const validFAQs = (faqList || [])
        .filter(faq => faq.question && String(faq.question).trim() && faq.answer && String(faq.answer).trim())
        .map(faq => ({ question: String(faq.question).trim(), answer: String(faq.answer).trim() }));
      let listToSave = productList || [];
      if (!productFormCollapsed && (product.productName?.trim() || editingProductId)) {
        const snapshot = getCurrentProductSnapshot();
        listToSave = editingProductId
          ? (productList || []).map(p => p.id === editingProductId ? snapshot : p)
          : [...(productList || []), snapshot];
      }
      const productDetailsPayload = {
        ...product,
        customizationRows,
        colourOptionsList,
        dimensionsList,
        leadTimeList,
        lead_time_list: validLeadTimes.length > 0 ? validLeadTimes : undefined,
        faqList,
        faq: validFAQs.length > 0 ? validFAQs : undefined,
        faq_list: validFAQs.length > 0 ? validFAQs : undefined,
        products: listToSave,
      };
      const payload = {
        facility_details: buildFacilityPayloadWithLocationLabels(facility),
        production_details: production,
        product_details: productDetailsPayload,
      };
      await CommonService.put(API_URLS.CONTRACT_MANUFACTURER.SAVE_PROFILE(companyId), payload);
      toast.success('Contract manufacturer profile saved successfully');
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err?.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (setter, field) => setter((prev) => ({ ...prev, [field]: !prev[field] }));

  const handleProductCategoryChange = (e) => {
    const value = e.target.value;
    setProduct((prev) => ({ ...prev, productCategoryId: value, productSubCategoryId: '', productSubSubCategoryId: '' }));
  };
  const handleProductSubcategoryChange = (e) => {
    const value = e.target.value;
    setProduct((prev) => ({ ...prev, productSubCategoryId: value, productSubSubCategoryId: '' }));
  };
  const handleProductSubSubcategoryChange = (e) => {
    const value = e.target.value;
    setProduct((prev) => ({ ...prev, productSubSubCategoryId: value }));
  };
  const addKeyword = () => {
    const v = keywordInput.trim();
    if (!v) return;
    const kw = Array.isArray(product.keywords) ? product.keywords : [];
    if (!kw.includes(v)) setProduct((prev) => ({ ...prev, keywords: [...(Array.isArray(prev.keywords) ? prev.keywords : []), v] }));
    setKeywordInput('');
  };
  const removeKeyword = (k) => {
    setProduct((prev) => ({ ...prev, keywords: (Array.isArray(prev.keywords) ? prev.keywords : []).filter((x) => x !== k) }));
  };
  const handleProductCatalogueUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingProductFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'product');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'product_catalogue');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduct((prev) => ({ ...prev, productCatalogueUrl: url }));
        toast.success('Product catalogue uploaded successfully');
      }
    } catch (err) {
      console.error('Product catalogue upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingProductFiles(false);
    }
    e.target.value = '';
  };
  const handleProductVideoUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingProductFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'product');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'product_video');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduct((prev) => ({ ...prev, productVideoUrl: url }));
        toast.success('Product video uploaded successfully');
      }
    } catch (err) {
      console.error('Product video upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingProductFiles(false);
    }
    e.target.value = '';
  };
  const handleProductCompanyLogoUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingProductFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'product');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'company_logo');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        setProduct((prev) => ({ ...prev, productCompanyLogoUrl: url }));
        toast.success('Company logo uploaded successfully');
      }
    } catch (err) {
      console.error('Company logo upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingProductFiles(false);
    }
    e.target.value = '';
  };

  const customizationTypesOptionsList = customizationTypesOptions || [];
  const getCustomizationVisibleRows = (customizationTypeValue) => {
    if (!customizationTypeValue) return [];
    const opts = customizationTypesOptionsList;
    const customizationOption = opts.find(opt => String(opt.value) === String(customizationTypeValue) || String(opt.label) === String(customizationTypeValue));
    const customizationLabel = (customizationOption?.label || customizationTypeValue).toString().toLowerCase();
    const customizationVisibilityMap = {
      'minor customization': ['logo', 'color', 'labels'],
      'minor': ['logo', 'color', 'labels'],
      'drawing': ['sketch', 'color'],
      'sample-based': ['sample_based'],
      'sample based': ['sample_based'],
      'sample': ['sample_based'],
      'full customization': ['logo', 'color', 'labels', 'packing', 'graphic'],
      'full': ['logo', 'color', 'labels', 'packing', 'graphic'],
    };
    const matchingKey = Object.keys(customizationVisibilityMap).find(key =>
      customizationLabel.includes(key.toLowerCase()) || key.toLowerCase() === customizationLabel
    );
    return matchingKey ? customizationVisibilityMap[matchingKey] : [];
  };
  const toggleCustomizationRow = (rowId) => {
    setCustomizationRows((rows) => rows.map(r => r.id === rowId ? { ...r, enabled: !r.enabled } : r));
  };
  const updateCustomizationField = (rowId, field, value) => {
    setCustomizationRows((rows) => rows.map(r => {
      if (r.id !== rowId) return r;
      const updated = { ...r, [field]: value };
      if (field === 'priceNoTax' || field === 'tax') {
        const priceNoTax = field === 'priceNoTax' ? parseFloat(value) : parseFloat(r.priceNoTax);
        const tax = field === 'tax' ? parseFloat(value) : parseFloat(r.tax);
        if (!isNaN(priceNoTax) && !isNaN(tax) && priceNoTax > 0 && tax >= 0) {
          updated.priceWithTax = (priceNoTax * (1 + tax / 100)).toFixed(2);
        } else {
          updated.priceWithTax = '';
        }
      }
      return updated;
    }));
  };
  const renderCustomizationInput = (row, field) => {
    const isEditable = row.enabled;
    const isReadOnly = field === 'priceWithTax';
    if (!isEditable || isReadOnly) {
      const displayValue = isReadOnly && row.enabled ? (row[field] || '') : '-';
      return <input className="input-pill input-center" value={displayValue} readOnly disabled={!isEditable} style={isReadOnly && row.enabled ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}} />;
    }
    return <input className="input-pill" placeholder="" value={row[field]} onChange={(e) => updateCustomizationField(row.id, field, e.target.value)} />;
  };
  const renderCustomizationDescCell = (row) => {
    if (!row.enabled) return <input className="input-pill input-center" value="-" readOnly disabled />;
    return (
      <div className="input-pill upload-pill" onClick={() => { setDescModal({ visible: true, rowId: row.id }); setDescText(row.desc || ''); setDescFiles([]); }}>
        <span className="upload-icon-only" aria-hidden>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
        </span>
      </div>
    );
  };
  const findOption = (options, val) => {
    if (!val || !options || !options.length) return null;
    const v = String(val).trim();
    if (!v) return null;
    return options.find(opt => String(opt.value) === v || String(opt.label) === v) || null;
  };

  const addColourOption = () => {
    const newId = Math.max(...colourOptionsList.map(c => c.id), 0) + 1;
    setColourOptionsList(prev => [...prev, { id: newId, color: '#ED00BE', imageUrl: '' }]);
  };
  const removeColourOption = (id) => {
    if (colourOptionsList.length > 1) {
      setColourOptionsList(prev => prev.filter(option => option.id !== id));
    }
  };
  const updateColourOption = (id, field, value) => {
    setColourOptionsList(prev => prev.map(option =>
      option.id === id ? { ...option, [field]: value } : option
    ));
  };
  const handleColourOptionImageUpload = async (optionId, e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    setUploadingProductFiles(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', 'product');
      formData.append('companyName', facility.companyName || companyProfile?.company_name || 'contract-manufacturer');
      formData.append('fileType', 'colour_image');
      const response = await CommonService.post('/upload/single', formData);
      const url = response?.data?.accessPath || response?.data?.filePath || response?.accessPath || '';
      if (url) {
        updateColourOption(optionId, 'imageUrl', url);
        toast.success('Color image uploaded successfully');
      }
    } catch (err) {
      console.error('Color image upload error:', err);
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingProductFiles(false);
    }
    e.target.value = '';
  };

  const addDimension = () => {
    const newId = Math.max(...dimensionsList.map(d => d.id), 0) + 1;
    setDimensionsList(prev => [...prev, { id: newId, height: '', length: '', width: '', weight: '', uom: '' }]);
  };
  const removeDimension = (id) => {
    if (dimensionsList.length > 1) {
      setDimensionsList(prev => prev.filter(dim => dim.id !== id));
    }
  };
  const updateDimension = (id, field, value) => {
    setDimensionsList(prev => prev.map(dim =>
      dim.id === id ? { ...dim, [field]: value } : dim
    ));
  };

  const addLeadTime = () => {
    const newId = Math.max(...leadTimeList.map(l => l.id), 0) + 1;
    setLeadTimeList(prev => [...prev, { id: newId, days: '', min: '', max: '' }]);
  };
  const removeLeadTime = (id) => {
    if (leadTimeList.length > 1) {
      setLeadTimeList(prev => prev.filter(lead => lead.id !== id));
    }
  };
  const updateLeadTime = (id, field, value) => {
    setLeadTimeList(prev => prev.map(lead =>
      lead.id === id ? { ...lead, [field]: value } : lead
    ));
  };

  const addFAQ = () => {
    const newId = Math.max(...faqList.map(f => f.id), 0) + 1;
    setFaqList(prev => [...prev, { id: newId, question: '', answer: '' }]);
  };
  const removeFAQ = (id) => {
    if (faqList.length > 1) {
      setFaqList(prev => prev.filter(faq => faq.id !== id));
    }
  };
  const updateFAQ = (id, field, value) => {
    setFaqList(prev => prev.map(faq =>
      faq.id === id ? { ...faq, [field]: value } : faq
    ));
  };

  const getCurrentProductSnapshot = () => ({
    id: editingProductId || Date.now(),
    ...product,
    customizationRows: [...(customizationRows || [])],
    colourOptionsList: [...(colourOptionsList || [])],
    dimensionsList: [...(dimensionsList || [])],
    leadTimeList: [...(leadTimeList || [])],
    faqList: [...(faqList || [])],
  });

  const resetProductForm = () => {
    setProduct({ ...defaultProduct });
    setCustomizationRows([
      { id: 'logo', name: 'LOGO', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'color', name: 'Color', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'labels', name: 'Labels', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'packing', name: 'Packing', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'graphic', name: 'Graphic', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'sketch', name: 'Sketch', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'sample_based', name: 'Sample-based', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    ]);
    setColourOptionsList([{ id: 1, color: '#ED00BE', imageUrl: '' }]);
    setDimensionsList([{ id: 1, height: '', length: '', width: '', weight: '', uom: '' }]);
    setLeadTimeList([{ id: 1, days: '', min: '', max: '' }]);
    setFaqList([{ id: 1, question: '', answer: '' }]);
    setKeywordInput('');
  };

  const handleAddProduct = () => {
    setProductFormCollapsed(false);
    setEditingProductId(null);
    resetProductForm();
  };

  const loadProductIntoForm = (entry) => {
    if (!entry) return;
    const { id, customizationRows: cr, colourOptionsList: col, dimensionsList: dl, leadTimeList: lt, faqList: fl, ...prod } = entry;
    setProduct({ ...defaultProduct, ...prod });
    setCustomizationRows(Array.isArray(cr) && cr.length > 0 ? cr : [
      { id: 'logo', name: 'LOGO', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'color', name: 'Color', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'labels', name: 'Labels', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'packing', name: 'Packing', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'graphic', name: 'Graphic', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'sketch', name: 'Sketch', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
      { id: 'sample_based', name: 'Sample-based', enabled: false, moq: '', priceNoTax: '', tax: '', priceWithTax: '', desc: '' },
    ]);
    setColourOptionsList(Array.isArray(col) && col.length > 0 ? col : [{ id: 1, color: '#ED00BE', imageUrl: '' }]);
    setDimensionsList(Array.isArray(dl) && dl.length > 0 ? dl : [{ id: 1, height: '', length: '', width: '', weight: '', uom: '' }]);
    setLeadTimeList(Array.isArray(lt) && lt.length > 0 ? lt : [{ id: 1, days: '', min: '', max: '' }]);
    setFaqList(Array.isArray(fl) && fl.length > 0 ? fl : [{ id: 1, question: '', answer: '' }]);
  };

  const handleEditProduct = (entry) => {
    loadProductIntoForm(entry);
    setEditingProductId(entry.id);
    setProductFormCollapsed(false);
  };

  const handleDeleteProduct = (id) => {
    setProductList(prev => prev.filter(p => p.id !== id));
    if (editingProductId === id) {
      setProductFormCollapsed(true);
      setEditingProductId(null);
      resetProductForm();
    }
  };

  const handleSaveCurrentProduct = () => {
    if (!validateProduct()) return;
    const snapshot = getCurrentProductSnapshot();
    if (editingProductId) {
      setProductList(prev => prev.map(p => p.id === editingProductId ? snapshot : p));
    } else {
      setProductList(prev => [...prev, snapshot]);
    }
    setProductFormCollapsed(true);
    setEditingProductId(null);
    resetProductForm();
    toast.success(editingProductId ? 'Product updated' : 'Product added to list');
  };

  const dimensionUnitOptionsList = dimensionUnitOptions || [];
  const priceUomOptions = useMemo(
    () => getPriceUomOptions(product.pricingModel, dimensionUnitOptionsList),
    [product.pricingModel, dimensionUnitOptionsList],
  );

  const addManufacturingCapability = () => {
    setProduction((prev) => ({
      ...prev,
      capabilitiesList: [...(prev.capabilitiesList || []), { ...defaultCapabilityEntry }],
    }));
  };

  const removeManufacturingCapability = (index) => {
    if ((production.capabilitiesList || []).length <= 1) return;
    setProduction((prev) => ({
      ...prev,
      capabilitiesList: (prev.capabilitiesList || []).filter((_, i) => i !== index),
    }));
  };

  const updateCapability = (index, field, value) => {
    setProduction((prev) => {
      const list = prev.capabilitiesList || [];
      if (index < 0 || index >= list.length) return prev;
      return {
        ...prev,
        capabilitiesList: list.map((cap, i) => (i === index ? { ...cap, [field]: value } : cap)),
      };
    });
  };

  const toggleCapability = (index, field) => {
    const list = production.capabilitiesList || [];
    const cap = list[index];
    if (!cap) return;
    updateCapability(index, field, !cap[field]);
  };

  if (loading) {
    return (
      <div className="seller-profile container-fluid">
        <div className="row"><main className="col-12 sp-main"><div className="text-center py-5">Loading...</div></main></div>
      </div>
    );
  }

  return (
    <div className="seller-profile contract-manufacturer-profile container-fluid">
      <div className="row">
        <main className="col-12 sp-main">
          <div className="sp-header-sticky">
            <nav aria-label="breadcrumb" className="sp-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/" className="breadcrumb-link">Home</a></li>
                <li className="breadcrumb-item active" aria-current="page">Facility Details</li>
              </ol>
            </nav>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="sp-main-title">Details of Contract Manufacturers</h1>
                <p className="sp-subtitle">Manage your facility, production, and product details</p>
              </div>
              <button type="button" className="btn btn-outline-primary" onClick={() => navigate('/seller-profile')}>Seller Profile</button>
            </div>
          </div>

          <Tabs className="sp-card" activeKey={activeTab} onChange={setActiveTab}>
            {/* Factory Details */}
            <TabPane
              tab={
                <span>
                  <span className="tab-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 20h20v-8H2v8Z"/><path d="M6 20v-4"/><path d="M10 20v-4"/><path d="M14 20v-4"/><path d="M18 20v-4"/><path d="M2 12h20V8l-10-4-10 4v4Z"/></svg>
                  </span>
                  Factory Details
                </span>
              }
              key="1"
            >
              <div className="container-fluid">
                <section className="sp-section">
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">Facility Details</h6>
                      <button type="button" className="btn-add" onClick={addFacilityDetailsEntry}>+ Add More</button>
                    </div>
                    {facilityDetailsList.map((fdEntry, index) => (
                      <div key={index} className="mb-4">
                        {facilityDetailsList.length > 1 && (
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="sp-section-subtitle">Facility {index + 1}</h6>
                            <button type="button" className="btn-icon-remove" onClick={() => removeFacilityDetailsEntry(index)} title="Remove facility">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        )}
                        <div className="row g-2 align-items-end" onFocusCapture={() => setLocationContextIndex(index)}>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Company Name *" value={fdEntry.companyName} onChange={(e) => updateFacilityDetailsEntry(index, 'companyName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Facility Name *" value={fdEntry.facilityName} onChange={(e) => updateFacilityDetailsEntry(index, 'facilityName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Company Reg No *" value={fdEntry.companyRegNo} onChange={(e) => updateFacilityDetailsEntry(index, 'companyRegNo', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Industry Type *"
                          value={industryTypeOptions.filter((opt) =>
                            Array.isArray(fdEntry.industryTypes) && fdEntry.industryTypes.includes(opt.value)
                          )}
                          onChange={(selected) => {
                            updateFacilityDetailsEntry(index, 'industryTypes', selected ? selected.map((s) => s.value) : []);
                          }}
                          options={industryTypeOptions}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={fdEntry.contractManufacturingType} onChange={(e) => updateFacilityDetailsEntry(index, 'contractManufacturingType', e.target.value)}>
                          <option value="">Contract Manufacturing Type *</option>
                          {CONTRACT_MANUFACTURING_TYPE_MAP.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={fdEntry.facilityType} onChange={(e) => updateFacilityDetailsEntry(index, 'facilityType', e.target.value)}>
                          <option value="">Facility Type *</option>
                          {FACILITY_TYPE_MAP.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Factory Licence Number *" value={fdEntry.factoryLicenceNumber} onChange={(e) => updateFacilityDetailsEntry(index, 'factoryLicenceNumber', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" type="date" placeholder="Factory licence validity period *" aria-label="Factory licence validity period" value={fdEntry.factoryLicenceValidityPeriod} onChange={(e) => updateFacilityDetailsEntry(index, 'factoryLicenceValidityPeriod', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Tax Id" value={fdEntry.taxId} onChange={(e) => updateFacilityDetailsEntry(index, 'taxId', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Export Import Code (Optional)" value={fdEntry.exportImportCode} onChange={(e) => updateFacilityDetailsEntry(index, 'exportImportCode', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Parent Company Or Group Ownership" value={fdEntry.parentCompanyOrGroupOwnership} onChange={(e) => updateFacilityDetailsEntry(index, 'parentCompanyOrGroupOwnership', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <textarea className="input-area" placeholder="Address *" value={fdEntry.address} onChange={(e) => updateFacilityDetailsEntry(index, 'address', e.target.value)} rows={2} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select className="input-pill" value={fdEntry.country} onChange={(e) => {
                          setLocationContextIndex(index);
                          updateFacilityDetailsEntry(index, 'country', e.target.value);
                        }}>
                          <option value="">Country *</option>
                          {countries.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select className="input-pill" value={fdEntry.state} onFocus={() => setLocationContextIndex(index)} onChange={(e) => updateFacilityDetailsEntry(index, 'state', e.target.value)}>
                          <option value="">State *</option>
                          {states.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select className="input-pill" value={fdEntry.city} onFocus={() => setLocationContextIndex(index)} onChange={(e) => updateFacilityDetailsEntry(index, 'city', e.target.value)}>
                          <option value="">City *</option>
                          {cities.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Pincode *" value={fdEntry.pincode} onChange={(e) => updateFacilityDetailsEntry(index, 'pincode', e.target.value)} />
                      </div>
                      <div className="col-12 mt-2">
                        <div className="file-upload-component">
                          <div className="file-title">Upload Facility Images (max 5)</div>
                          <label className={`file-box ${(fdEntry.facilityImages?.length ?? 0) >= MAX_FACILITY_IMAGES ? 'disabled' : ''}`}>
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              </svg>
                              <span className="choose-text">
                                {uploadingFacilityImages && uploadingFacilityImageEntry === index ? 'Uploading...' : (fdEntry.facilityImages?.length ?? 0) >= MAX_FACILITY_IMAGES ? 'Max 5 images' : 'Choose file'}
                              </span>
                            </div>
                            <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp" onChange={(e) => uploadFacilityImage(e, index)} disabled={uploadingFacilityImages || (fdEntry.facilityImages?.length ?? 0) >= MAX_FACILITY_IMAGES} style={{ display: 'none' }} />
                          </label>
                          {Array.isArray(fdEntry.facilityImages) && fdEntry.facilityImages.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {fdEntry.facilityImages.map((url, idx) => (
                                <div key={idx} className="position-relative d-inline-block">
                                  <img src={resolvePublicFileUrl(url)} alt={`Facility ${idx + 1}`} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6 }} />
                                  <button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0" style={{ padding: '2px 6px', fontSize: 10 }} onClick={() => removeFacilityImage(idx, index)} aria-label="Remove">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                      </div>
                    ))}
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Contact Information</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Name *" value={facility.contactName} onChange={(e) => updateFacility('contactName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" type="email" placeholder="Email" value={facility.contactEmail} onChange={(e) => updateFacility('contactEmail', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Contact" value={facility.contactPhone} onChange={(e) => updateFacility('contactPhone', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Designation" value={facility.contactDesignation} onChange={(e) => updateFacility('contactDesignation', e.target.value)} />
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Alternate Contact Details</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Alternate Name" value={facility.altContactName} onChange={(e) => updateFacility('altContactName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" type="email" placeholder="Alternate Email" value={facility.altContactEmail} onChange={(e) => updateFacility('altContactEmail', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Alternate Contact" value={facility.altContactPhone} onChange={(e) => updateFacility('altContactPhone', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Alternate Designation" value={facility.altContactDesignation} onChange={(e) => updateFacility('altContactDesignation', e.target.value)} />
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">Ownership and Compliance *</h6>
                      <button type="button" className="btn-add" onClick={addOwnershipComplianceEntry}>+ Add More</button>
                    </div>
                    {ownershipComplianceList.map((ocEntry, index) => (
                      <div key={index} className="mb-4">
                        {ownershipComplianceList.length > 1 && (
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="sp-section-subtitle">Ownership {index + 1}</h6>
                            <button type="button" className="btn-icon-remove" onClick={() => removeOwnershipComplianceEntry(index)} title="Remove ownership entry">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        )}
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={ocEntry.ownershipType} onChange={(e) => {
                          const v = e.target.value;
                          updateOwnershipComplianceEntry(index, 'ownershipType', v);
                          if (v !== 'leased') updateOwnershipComplianceEntry(index, 'leaseType', '');
                        }}>
                          <option value="">Ownership Type</option>
                          {OWNERSHIP_TYPE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      {ocEntry.ownershipType === 'leased' && (
                        <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                          <select className="input-pill" value={ocEntry.leaseType} onChange={(e) => updateOwnershipComplianceEntry(index, 'leaseType', e.target.value)}>
                            <option value="">Lease Type</option>
                            {LEASE_TYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Insurance Coverage"
                          value={INSURANCE_COVERAGE_OPTIONS.filter((opt) =>
                            Array.isArray(ocEntry.insuranceCoverage) && ocEntry.insuranceCoverage.includes(opt.value)
                          )}
                          onChange={(selected) => updateOwnershipComplianceEntry(index, 'insuranceCoverage', selected ? selected.map((s) => s.value) : [])}
                          options={INSURANCE_COVERAGE_OPTIONS}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Regulatory Approvals"
                          value={REGULATORY_APPROVALS_OPTIONS.filter((opt) =>
                            Array.isArray(ocEntry.regulatoryApprovals) && ocEntry.regulatoryApprovals.includes(opt.value)
                          )}
                          onChange={(selected) => updateOwnershipComplianceEntry(index, 'regulatoryApprovals', selected ? selected.map((s) => s.value) : [])}
                          options={REGULATORY_APPROVALS_OPTIONS}
                          isClearable
                        />
                      </div>
                      {ocEntry.ownershipType !== 'leased' && (
                        <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                          <Select
                            className="input-pill p-0 cm-select"
                            isMulti
                            placeholder="Compliance Certificate"
                            value={COMPLIANCE_CERTIFICATE_OPTIONS.filter((opt) =>
                              Array.isArray(ocEntry.complianceCertificate) && ocEntry.complianceCertificate.includes(opt.value)
                            )}
                            onChange={(selected) => updateOwnershipComplianceEntry(index, 'complianceCertificate', selected ? selected.map((s) => s.value) : [])}
                            options={COMPLIANCE_CERTIFICATE_OPTIONS}
                            isClearable
                          />
                        </div>
                      )}
                    </div>
                    <div className="row g-2 mt-2 align-items-end">
                      {ocEntry.ownershipType === 'leased' && (
                        <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                          <Select
                            className="input-pill p-0 cm-select"
                            isMulti
                            placeholder="Compliance Certificate"
                            value={COMPLIANCE_CERTIFICATE_OPTIONS.filter((opt) =>
                              Array.isArray(ocEntry.complianceCertificate) && ocEntry.complianceCertificate.includes(opt.value)
                            )}
                            onChange={(selected) => updateOwnershipComplianceEntry(index, 'complianceCertificate', selected ? selected.map((s) => s.value) : [])}
                            options={COMPLIANCE_CERTIFICATE_OPTIONS}
                            isClearable
                          />
                        </div>
                      )}
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-pill input-pill-toggle">
                          <span>Environmental Clearances</span>
                          <div
                            className={`sp-switch ${ocEntry.environmentalClearances ? 'on' : 'off'}`}
                            role="switch"
                            aria-checked={!!ocEntry.environmentalClearances}
                            onClick={() => updateOwnershipComplianceEntry(index, 'environmentalClearances', !ocEntry.environmentalClearances)}
                          >
                            <span className="sp-switch-label">{ocEntry.environmentalClearances ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="file-upload-component">
                          <label className={`file-box ${(ocEntry.complianceCertificates?.length ?? 0) >= MAX_COMPLIANCE_CERTIFICATES ? 'disabled' : ''}`}>
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              </svg>
                              <span className="choose-text">{uploadingComplianceCertificates && uploadingComplianceEntry === index ? 'Uploading...' : (ocEntry.complianceCertificates?.length ?? 0) >= MAX_COMPLIANCE_CERTIFICATES ? 'Max 5 certificates' : 'Upload Certificates (max 5)'}</span>
                            </div>
                            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => uploadComplianceCertificate(e, index)} disabled={uploadingComplianceCertificates || (ocEntry.complianceCertificates?.length ?? 0) >= MAX_COMPLIANCE_CERTIFICATES} style={{ display: 'none' }} />
                          </label>
                          {Array.isArray(ocEntry.complianceCertificates) && ocEntry.complianceCertificates.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {ocEntry.complianceCertificates.map((url, idx) => (
                                <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                  <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 80 }}>Cert {idx + 1}</a>
                                  <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeComplianceCertificate(idx, index)} aria-label="Remove">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                      </div>
                    ))}
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Infrastructure Details *</h6>
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={facility.typeOfConstruction} onChange={(e) => updateFacility('typeOfConstruction', e.target.value)}>
                          <option value="">Type of Construction *</option>
                          {TYPE_OF_CONSTRUCTION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Number of Floors" value={facility.numberOfFloors} onChange={(e) => updateFacility('numberOfFloors', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Year of Construction" value={facility.yearOfConstruction} onChange={(e) => updateFacility('yearOfConstruction', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-total-facility-area"
                          placeholder="Total Facility Area *"
                          ariaLabel="Total Facility Area"
                          value={facility.totalFacilityArea}
                          unit={facility.totalFacilityAreaUnit}
                          defaultUnit="sq_feet"
                          resolveUnit={resolveFacilityAreaUnit}
                          unitOptions={FACILITY_AREA_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('totalFacilityArea', v)}
                          onUnitChange={(v) => updateFacility('totalFacilityAreaUnit', v)}
                        />
                      </div>
                    </div>
                    <div className="row g-2 mt-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={facility.accessibility} onChange={(e) => updateFacility('accessibility', e.target.value)}>
                          <option value="">Accessibility</option>
                          {ACCESSIBILITY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-build-up-area"
                          placeholder="Build-Up Area"
                          ariaLabel="Build-Up Area"
                          value={facility.buildUpArea}
                          unit={facility.buildUpAreaUnit}
                          defaultUnit="sq_feet"
                          resolveUnit={resolveFacilityAreaUnit}
                          unitOptions={FACILITY_AREA_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('buildUpArea', v)}
                          onUnitChange={(v) => updateFacility('buildUpAreaUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={facility.conditionOfFacility} onChange={(e) => updateFacility('conditionOfFacility', e.target.value)}>
                          <option value="">Condition of Facility / Asset</option>
                          {CONDITION_OF_FACILITY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Utility Details *</h6>
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <select className="input-pill" value={facility.primaryPowerSource} onChange={(e) => updateFacility('primaryPowerSource', e.target.value)}>
                          <option value="">Primary Power Source *</option>
                          {PRIMARY_POWER_SOURCE_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-sanctioned-power-load"
                          placeholder="Sanctioned Power Load"
                          ariaLabel="Sanctioned Power Load"
                          value={facility.sanctionedPowerLoad}
                          unit={facility.sanctionedPowerLoadUnit}
                          defaultUnit="kva"
                          unitOptions={SANCTIONED_POWER_LOAD_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('sanctionedPowerLoad', v)}
                          onUnitChange={(v) => updateFacility('sanctionedPowerLoadUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-power-supply-capacity"
                          placeholder="Power Supply Capacity"
                          ariaLabel="Power Supply Capacity"
                          value={facility.powerSupplyCapacity}
                          unit={facility.powerSupplyCapacityUnit}
                          defaultUnit="kva"
                          unitOptions={SANCTIONED_POWER_LOAD_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('powerSupplyCapacity', v)}
                          onUnitChange={(v) => updateFacility('powerSupplyCapacityUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-pill input-pill-toggle">
                          <span>Power Backup</span>
                          <div className={`sp-switch ${facility.powerBackup ? 'on' : 'off'}`} role="switch" aria-checked={!!facility.powerBackup} onClick={() => setFacility((prev) => ({ ...prev, powerBackup: !prev.powerBackup, ...(prev.powerBackup ? { powerType: '' } : {}) }))}>
                            <span className="sp-switch-label">{facility.powerBackup ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row g-2 mt-2 align-items-end">
                      {facility.powerBackup && (
                        <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                          <input className="input-pill" placeholder="Power Type" value={facility.powerType} onChange={(e) => updateFacility('powerType', e.target.value)} />
                        </div>
                      )}
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-water-supply-capacity"
                          placeholder="Water Supply Capacity ( Litres)"
                          ariaLabel="Water Supply Capacity"
                          value={facility.waterSupplyCapacity}
                          unit={facility.waterSupplyCapacityUnit}
                          defaultUnit="hour"
                          unitOptions={WATER_SUPPLY_CAPACITY_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('waterSupplyCapacity', v)}
                          onUnitChange={(v) => updateFacility('waterSupplyCapacityUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-pill input-pill-toggle">
                          <span>Waste Water Treatment</span>
                          <div className={`sp-switch ${facility.wasteWaterTreatment ? 'on' : 'off'}`} role="switch" aria-checked={!!facility.wasteWaterTreatment} onClick={() => setFacility((prev) => ({ ...prev, wasteWaterTreatment: !prev.wasteWaterTreatment, ...(prev.wasteWaterTreatment ? { wasteWaterTreatmentDetails: '' } : {}) }))}>
                            <span className="sp-switch-label">{facility.wasteWaterTreatment ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-pill input-pill-toggle">
                          <span>Waste Management System</span>
                          <div className={`sp-switch ${facility.wasteManagementSystem ? 'on' : 'off'}`} role="switch" aria-checked={!!facility.wasteManagementSystem} onClick={() => setFacility((prev) => ({ ...prev, wasteManagementSystem: !prev.wasteManagementSystem, ...(prev.wasteManagementSystem ? { wasteManagementSystemDetails: '' } : {}) }))}>
                            <span className="sp-switch-label">{facility.wasteManagementSystem ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {facility.wasteManagementSystem && (
                      <div className="row g-2 mt-2">
                        <div className="col-12">
                          <textarea className="input-area input-pill" placeholder="Please Enter Waste Management Details" value={facility.wasteManagementSystemDetails || ''} onChange={(e) => updateFacility('wasteManagementSystemDetails', e.target.value)} rows={4} />
                        </div>
                      </div>
                    )}
                    {facility.wasteWaterTreatment && (
                      <div className="row g-2 mt-2">
                        <div className="col-12">
                          <textarea className="input-area input-pill" placeholder="Please Enter Waste Water treatment details" value={facility.wasteWaterTreatmentDetails || ''} onChange={(e) => updateFacility('wasteWaterTreatmentDetails', e.target.value)} rows={4} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Storage Facility Capacity *</h6>
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-raw-material-warehouse"
                          placeholder="Raw material Warehouse *"
                          ariaLabel="Raw material Warehouse"
                          value={facility.rawMaterialWarehouse}
                          unit={facility.rawMaterialWarehouseUnit}
                          defaultUnit="sq_ft"
                          resolveUnit={resolveStorageWarehouseUnit}
                          unitOptions={STORAGE_WAREHOUSE_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('rawMaterialWarehouse', v)}
                          onUnitChange={(v) => updateFacility('rawMaterialWarehouseUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-cold-storage"
                          placeholder="Cold Storage"
                          ariaLabel="Cold Storage"
                          value={facility.coldStorage}
                          unit={facility.coldStorageUnit}
                          defaultUnit="mt"
                          resolveUnit={resolveColdStorageUnit}
                          unitOptions={COLD_STORAGE_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('coldStorage', v)}
                          onUnitChange={(v) => updateFacility('coldStorageUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <NumberWithUnit
                          inputId="cm-finished-goods-storage"
                          placeholder="Finished Goods Storage"
                          ariaLabel="Finished Goods Storage"
                          value={facility.finishedGoodsStorage}
                          unit={facility.finishedGoodsStorageUnit}
                          defaultUnit="sq_ft"
                          resolveUnit={resolveStorageWarehouseUnit}
                          unitOptions={STORAGE_WAREHOUSE_UNIT_OPTIONS}
                          onValueChange={(v) => updateFacility('finishedGoodsStorage', v)}
                          onUnitChange={(v) => updateFacility('finishedGoodsStorageUnit', v)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Fire & Safety Equipments"
                          value={FIRE_SAFETY_EQUIPMENT_OPTIONS.filter((opt) =>
                            Array.isArray(facility.fireSafetyEquipments) && facility.fireSafetyEquipments.includes(opt.value)
                          )}
                          onChange={(selected) => updateFacility('fireSafetyEquipments', selected ? selected.map((s) => s.value) : [])}
                          options={FIRE_SAFETY_EQUIPMENT_OPTIONS}
                          isClearable
                        />
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">🚚 Supply Chain Logistics</h6>
                    <div className="row g-2 align-items-end">
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Enter port name" value={facility.portName} onChange={(e) => updateFacility('portName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-group-prefix-suffix input-group-row">
                          <input type="number" min="0" step="any" className="input-middle input-middle-with-border" placeholder="Enter Distance to the Factory" value={facility.portDistanceKm} onChange={(e) => updateFacility('portDistanceKm', e.target.value)} />
                          <div className="input-prefix input-prefix-no-borders">
                            <select value={facility.portDistanceUnit || 'km'} onChange={(e) => updateFacility('portDistanceUnit', e.target.value)}>
                              {DISTANCE_UNIT_OPTIONS.map((u) => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Enter airport name" value={facility.airportName} onChange={(e) => updateFacility('airportName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-group-prefix-suffix input-group-row">
                          <input type="number" min="0" step="any" className="input-middle input-middle-with-border" placeholder="Enter Distance to Port/Airport" value={facility.airportDistanceKm} onChange={(e) => updateFacility('airportDistanceKm', e.target.value)} />
                          <div className="input-prefix input-prefix-no-borders">
                            <select value={facility.airportDistanceUnit || 'km'} onChange={(e) => updateFacility('airportDistanceUnit', e.target.value)}>
                              {DISTANCE_UNIT_OPTIONS.map((u) => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <input className="input-pill" placeholder="Enter highway name" value={facility.highwayName} onChange={(e) => updateFacility('highwayName', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3 cm-field-wrap">
                        <div className="input-group-prefix-suffix input-group-row">
                          <input type="number" min="0" step="any" className="input-middle input-middle-with-border" placeholder="Enter Distance to the Factory" value={facility.highwayDistanceKm} onChange={(e) => updateFacility('highwayDistanceKm', e.target.value)} />
                          <div className="input-prefix input-prefix-no-borders">
                            <select value={facility.highwayDistanceUnit || 'km'} onChange={(e) => updateFacility('highwayDistanceUnit', e.target.value)}>
                              {DISTANCE_UNIT_OPTIONS.map((u) => (
                                <option key={u.value} value={u.value}>{u.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Financial & Risk Indicator</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Annual Turnover Bracket *" value={facility.annualTurnoverBracket} onChange={(e) => updateFacility('annualTurnoverBracket', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Export % vs Domestic % Split *" value={facility.exportDomesticSplit} onChange={(e) => updateFacility('exportDomesticSplit', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Risk Indicator</span>
                          <div className={`sp-switch ${facility.riskIndicator ? 'on' : 'off'}`} role="switch" aria-checked={!!facility.riskIndicator} onClick={() => toggle(setFacility, 'riskIndicator')}>
                            <span className="sp-switch-label">{facility.riskIndicator ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <textarea className="input-area" placeholder="Major Clients" value={facility.majorClients} onChange={(e) => updateFacility('majorClients', e.target.value)} rows={2} />
                      </div>
                      <div className="col-12 mt-2">
                        <div className="file-upload-component">
                          <label className="file-box">
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              </svg>
                              <span className="choose-text">{uploadingMajorClientLogo ? 'Uploading...' : 'Upload Major Client Logo'}</span>
                            </div>
                            <input type="file" accept=".png,.jpg,.jpeg,.gif,.webp" onChange={uploadMajorClientLogo} disabled={uploadingMajorClientLogo} style={{ display: 'none' }} />
                          </label>
                          {facility.majorClientLogo && (
                            <div className="d-flex align-items-center gap-2 mt-2">
                              <img src={resolvePublicFileUrl(facility.majorClientLogo)} alt="Major client logo" style={{ width: 60, height: 60, objectFit: 'contain', borderRadius: 6, background: '#f5f5f5' }} />
                              <button type="button" className="btn btn-sm btn-outline-danger" onClick={removeMajorClientLogo}>Remove logo</button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </TabPane>

            {/* Production Details */}
            <TabPane
              tab={
                <span>
                  <span className="tab-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                  </span>
                  Production Details
                </span>
              }
              key="2"
            >
              <div className="container-fluid">
                <section className="sp-section">
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">Manufacturing Capabilities</h6>
                      <button type="button" className="btn-add" onClick={addManufacturingCapability}>+ Add More</button>
                    </div>
                    {(production.capabilitiesList || []).map((cap, index) => (
                      <div key={index} className="mb-4">
                        {(production.capabilitiesList || []).length > 1 && (
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="sp-section-subtitle">Capability {index + 1}</h6>
                            <button type="button" className="btn-icon-remove" onClick={() => removeManufacturingCapability(index)} title="Remove capability">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          </div>
                        )}
                        <div className="row g-2">
                          <div className="col-12 col-md-6 col-lg-3">
                            <CapabilityMultiSelect
                              placeholder="Manufacturing Process Type *"
                              options={MANUFACTURING_PROCESS_TYPE_OPTIONS}
                              value={cap.manufacturingProcessType || []}
                              onChange={(vals) => updateCapability(index, 'manufacturingProcessType', vals)}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <CapabilityMultiSelect
                              placeholder="Processes *"
                              options={PROCESS_OPTIONS}
                              value={cap.processes || []}
                              onChange={(vals) => updateCapability(index, 'processes', vals)}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <CapabilityMultiSelect
                              placeholder="Label"
                              options={LABEL_OPTIONS}
                              value={cap.label || []}
                              onChange={(vals) => updateCapability(index, 'label', vals)}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <CapabilityMultiSelect
                              placeholder="Technical Expertise"
                              options={TECHNICAL_EXPERTISE_OPTIONS}
                              value={cap.technicalExpertise || []}
                              onChange={(vals) => updateCapability(index, 'technicalExpertise', vals)}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <input className="input-pill" placeholder="Number of Production unit" value={cap.numberOfProductionUnit || ''} onChange={(e) => updateCapability(index, 'numberOfProductionUnit', e.target.value)} />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <Select
                              className="input-pill p-0 cm-select"
                              placeholder="Batch Size *"
                              options={BATCH_SIZE_OPTIONS}
                              value={findSelectOption(BATCH_SIZE_OPTIONS, cap.batchSize)}
                              onChange={(opt) => updateCapability(index, 'batchSize', opt?.value ?? '')}
                              isClearable
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <div className="input-pill input-pill-toggle">
                              <span>R&D Capabilities</span>
                              <div className={`sp-switch ${cap.rndCapabilities ? 'on' : 'off'}`} role="switch" aria-checked={!!cap.rndCapabilities} onClick={() => toggleCapability(index, 'rndCapabilities')}>
                                <span className="sp-switch-label">{cap.rndCapabilities ? 'Yes' : 'No'}</span>
                                <span className="sp-switch-thumb"></span>
                              </div>
                            </div>
                          </div>
                          {cap.rndCapabilities && (
                            <div className="col-12">
                              <textarea
                                className="input-area"
                                placeholder="Please Enter Capabilities Details"
                                value={cap.rndCapabilitiesDetails || ''}
                                onChange={(e) => updateCapability(index, 'rndCapabilitiesDetails', e.target.value)}
                                rows={4}
                                style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 16px' }}
                              />
                            </div>
                          )}
                          <div className="col-12 col-md-6 col-lg-3">
                            <input className="input-pill" placeholder="Machine Name" value={cap.machineName || ''} onChange={(e) => updateCapability(index, 'machineName', e.target.value)} />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <input className="input-pill" placeholder="Year of Purchase" value={cap.yearOfPurchase || ''} onChange={(e) => updateCapability(index, 'yearOfPurchase', e.target.value)} />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <input className="input-pill" placeholder="Make & Model" value={cap.makeAndModel || ''} onChange={(e) => updateCapability(index, 'makeAndModel', e.target.value)} />
                          </div>
                          <div className="col-12 col-md-6 col-lg-3">
                            <CapabilityMultiSelect
                              placeholder="Operational Status"
                              options={OPERATIONAL_STATUS_OPTIONS}
                              value={cap.operationalStatus || []}
                              onChange={(vals) => updateCapability(index, 'operationalStatus', vals)}
                            />
                          </div>
                          <div className="col-12 col-md-6 col-lg-4">
                            <MachineCapacityInput
                              value={cap.machineCapacityValue}
                              unit={cap.machineCapacity}
                              period={cap.machineCapacityPeriod}
                              onValueChange={(val) => updateCapability(index, 'machineCapacityValue', val)}
                              onUnitChange={(val) => updateCapability(index, 'machineCapacity', val)}
                              onPeriodChange={(val) => updateCapability(index, 'machineCapacityPeriod', val)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">
                      <span className="tab-icon me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                      </span>
                      Production & Operations
                    </h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Production line Layouts"
                          options={PRODUCTION_LINE_LAYOUTS_OPTIONS}
                          value={findSelectOption(PRODUCTION_LINE_LAYOUTS_OPTIONS, production.productionLineLayouts)}
                          onChange={(opt) => updateProduction('productionLineLayouts', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Automation Level"
                          options={selectOptionsForPicker(AUTOMATION_LEVEL_OPTIONS)}
                          value={findSelectOption(AUTOMATION_LEVEL_OPTIONS, production.automationLevel)}
                          onChange={(opt) => updateProduction('automationLevel', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <PercentInput
                          placeholder="Capacity Utilization"
                          value={production.capacityUtilization}
                          onChange={(val) => updateProduction('capacityUtilization', val)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Maintenance Schedule"
                          options={selectOptionsForPicker(MAINTENANCE_SCHEDULE_OPTIONS)}
                          value={findSelectOption(MAINTENANCE_SCHEDULE_OPTIONS, production.maintenanceSchedule)}
                          onChange={(opt) => updateProduction('maintenanceSchedule', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Downtime"
                          options={selectOptionsForPicker(DOWNTIME_OPTIONS)}
                          value={findSelectOption(DOWNTIME_OPTIONS, production.downtime)}
                          onChange={(opt) => updateProduction('downtime', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <PercentInput
                          placeholder="Rejection Rate"
                          value={production.rejectionRate}
                          onChange={(val) => updateProduction('rejectionRate', val)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="d-flex gap-1 align-items-center">
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="input-pill flex-grow-1"
                            style={{ minWidth: 0 }}
                            placeholder="Future Expansion Capacity"
                            value={production.futureExpansionCapacityValue ?? ''}
                            onChange={(e) => updateProduction('futureExpansionCapacityValue', e.target.value)}
                          />
                          <Select
                            className="input-pill p-0 cm-select flex-grow-1"
                            style={{ minWidth: 0 }}
                            placeholder="Unit"
                            options={FUTURE_EXPANSION_UNIT_OPTIONS}
                            value={FUTURE_EXPANSION_UNIT_OPTIONS.find((o) => o.value === production.futureExpansionCapacity) || null}
                            onChange={(opt) => updateProduction('futureExpansionCapacity', opt?.value ?? '')}
                            isClearable
                          />
                          <Select
                            className="input-pill p-0 cm-select flex-grow-1"
                            style={{ minWidth: 0 }}
                            placeholder="Capacity"
                            options={FUTURE_EXPANSION_PERIOD_OPTIONS}
                            value={FUTURE_EXPANSION_PERIOD_OPTIONS.find((o) => o.value === production.futureExpansionCapacityUnit) || null}
                            onChange={(opt) => updateProduction('futureExpansionCapacityUnit', opt?.value ?? '')}
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">
                      <span className="tab-icon me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 11l3 3L22 4"></path>
                          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                        </svg>
                      </span>
                      Sustainability Practices
                    </h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Sustainability Certificate"
                          options={SUSTAINABILITY_CERTIFICATE_OPTIONS}
                          value={SUSTAINABILITY_CERTIFICATE_OPTIONS.filter((o) => (production.sustainabilityCertificate || []).includes(o.value))}
                          onChange={(opts) => updateProduction('sustainabilityCertificate', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Use of Renewable energy"
                          options={USE_OF_RENEWABLE_ENERGY_OPTIONS}
                          value={USE_OF_RENEWABLE_ENERGY_OPTIONS.find((o) => o.value === production.useOfRenewableEnergy) || null}
                          onChange={(opt) => updateProduction('useOfRenewableEnergy', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Emission Reduction Target *"
                          options={EMISSION_REDUCTION_TARGET_OPTIONS}
                          value={EMISSION_REDUCTION_TARGET_OPTIONS.find((o) => o.value === production.emissionReductionTarget) || null}
                          onChange={(opt) => updateProduction('emissionReductionTarget', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Waste Management"
                          options={WASTE_MANAGEMENT_OPTIONS}
                          value={WASTE_MANAGEMENT_OPTIONS.find((o) => o.value === production.wasteManagement) || null}
                          onChange={(opt) => updateProduction('wasteManagement', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Packaging Sustainability *"
                          options={PACKAGING_SUSTAINABILITY_OPTIONS}
                          value={PACKAGING_SUSTAINABILITY_OPTIONS.find((o) => o.value === production.packagingSustainability) || null}
                          onChange={(opt) => updateProduction('packagingSustainability', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Responsible Sourcing"
                          options={RESPONSIBLE_SOURCING_OPTIONS}
                          value={RESPONSIBLE_SOURCING_OPTIONS.filter((o) => (production.responsibleSourcing || []).includes(o.value))}
                          onChange={(opts) => updateProduction('responsibleSourcing', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Water Use & Conversion"
                          options={WATER_USE_CONVERSION_OPTIONS}
                          value={WATER_USE_CONVERSION_OPTIONS.find((o) => o.value === production.waterUseConversion) || null}
                          onChange={(opt) => updateProduction('waterUseConversion', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Social / Labor Practices"
                          options={SOCIAL_LABOR_PRACTICES_OPTIONS}
                          value={SOCIAL_LABOR_PRACTICES_OPTIONS.filter((o) => (production.socialLaborPractices || []).includes(o.value))}
                          onChange={(opts) => updateProduction('socialLaborPractices', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Environmental Impact"
                          options={ENVIRONMENTAL_IMPACT_OPTIONS}
                          value={ENVIRONMENTAL_IMPACT_OPTIONS.filter((o) => (production.environmentalImpact || []).includes(o.value))}
                          onChange={(opts) => updateProduction('environmentalImpact', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12">
                        <textarea
                          className="input-area"
                          placeholder="Description *"
                          value={production.sustainabilityDescription ?? ''}
                          onChange={(e) => updateProduction('sustainabilityDescription', e.target.value)}
                          rows={4}
                          style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 16px' }}
                        />
                      </div>
                      <div className="col-12">
                        <div className="d-flex flex-column gap-3 mt-2">
                          <div className="file-upload-component">
                            <label className="file-box">
                              <div className="file-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                  <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                </svg>
                                <span className="choose-text">{uploadingSustainabilityDoc === 'supplier' ? 'Uploading...' : 'Upload Supplier Certifications'}</span>
                              </div>
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={uploadSustainabilityDoc('supplier')} disabled={uploadingSustainabilityDoc !== null} style={{ display: 'none' }} />
                            </label>
                            {Array.isArray(production.supplierCertifications) && production.supplierCertifications.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {production.supplierCertifications.map((url, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                    <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>Doc {idx + 1}</a>
                                    <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeSustainabilityDoc('supplier', idx)} aria-label="Remove">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="file-upload-component">
                            <label className="file-box">
                              <div className="file-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                  <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                </svg>
                                <span className="choose-text">{uploadingSustainabilityDoc === 'traceability' ? 'Uploading...' : 'Upload Traceability Reports or Supply chain audit Summaries'}</span>
                              </div>
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={uploadSustainabilityDoc('traceability')} disabled={uploadingSustainabilityDoc !== null} style={{ display: 'none' }} />
                            </label>
                            {Array.isArray(production.traceabilityReports) && production.traceabilityReports.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {production.traceabilityReports.map((url, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                    <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>Doc {idx + 1}</a>
                                    <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeSustainabilityDoc('traceability', idx)} aria-label="Remove">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="file-upload-component">
                            <label className="file-box">
                              <div className="file-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                  <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                </svg>
                                <span className="choose-text">{uploadingSustainabilityDoc === 'transparency' ? 'Uploading...' : 'Provide any Official Documentation that proves Transparency Practices'}</span>
                              </div>
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={uploadSustainabilityDoc('transparency')} disabled={uploadingSustainabilityDoc !== null} style={{ display: 'none' }} />
                            </label>
                            {Array.isArray(production.transparencyDocumentation) && production.transparencyDocumentation.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {production.transparencyDocumentation.map((url, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                    <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>Doc {idx + 1}</a>
                                    <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeSustainabilityDoc('transparency', idx)} aria-label="Remove">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">
                      <span className="tab-icon me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                          <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                      </span>
                      Ethical Practices
                    </h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Labour Practices"
                          options={LABOUR_PRACTICES_OPTIONS}
                          value={LABOUR_PRACTICES_OPTIONS.filter((o) => (production.labourPractices || []).includes(o.value))}
                          onChange={(opts) => updateProduction('labourPractices', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Grievance Reporting Mechanism" value={production.grievanceReportingMechanism} onChange={(e) => updateProduction('grievanceReportingMechanism', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Audit & compliance"
                          options={AUDIT_COMPLIANCE_OPTIONS}
                          value={AUDIT_COMPLIANCE_OPTIONS.filter((o) => (production.auditCompliance || []).includes(o.value))}
                          onChange={(opts) => updateProduction('auditCompliance', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Training & Capacity Building" value={production.trainingCapacityBuilding} onChange={(e) => updateProduction('trainingCapacityBuilding', e.target.value)} />
                      </div>
                      <div className="col-12">
                        <textarea className="input-area" placeholder="Description" value={production.ethicalDescription} onChange={(e) => updateProduction('ethicalDescription', e.target.value)} rows={4} style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 16px' }} />
                      </div>
                      <div className="col-12">
                        <div className="d-flex flex-column gap-3 mt-2">
                          <div className="file-upload-component">
                            <label className="file-box">
                              <div className="file-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                  <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                </svg>
                                <span className="choose-text">{uploadingEthicalDoc === 'codeOfConduct' ? 'Uploading...' : 'Upload the Manufacturers code of conduct that Outlines their commitment to ethical practices'}</span>
                              </div>
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={uploadEthicalDoc('codeOfConduct')} disabled={uploadingEthicalDoc !== null} style={{ display: 'none' }} />
                            </label>
                            {Array.isArray(production.codeOfConductDocuments) && production.codeOfConductDocuments.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {production.codeOfConductDocuments.map((url, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                    <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>Doc {idx + 1}</a>
                                    <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeEthicalDoc('codeOfConduct', idx)} aria-label="Remove">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="file-upload-component">
                            <label className="file-box">
                              <div className="file-content">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                  <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                </svg>
                                <span className="choose-text">{uploadingEthicalDoc === 'ethicalSourcing' ? 'Uploading...' : 'Upload the Manufacturers ethical Sourcing Policy Documents'}</span>
                              </div>
                              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={uploadEthicalDoc('ethicalSourcing')} disabled={uploadingEthicalDoc !== null} style={{ display: 'none' }} />
                            </label>
                            {Array.isArray(production.ethicalSourcingPolicyDocuments) && production.ethicalSourcingPolicyDocuments.length > 0 && (
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                {production.ethicalSourcingPolicyDocuments.map((url, idx) => (
                                  <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                    <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>Doc {idx + 1}</a>
                                    <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeEthicalDoc('ethicalSourcing', idx)} aria-label="Remove">×</button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">Quality Control</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Quality Control System" value={production.qualityControlSystem} onChange={(e) => updateProduction('qualityControlSystem', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          isMulti
                          placeholder="Quality Certification Available *"
                          options={QUALITY_CERTIFICATION_AVAILABLE_OPTIONS}
                          value={QUALITY_CERTIFICATION_AVAILABLE_OPTIONS.filter((o) => (production.qualityCertificationAvailable || []).includes(o.value))}
                          onChange={(opts) => updateProduction('qualityCertificationAvailable', opts ? opts.map((o) => o.value) : [])}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0 cm-select"
                          placeholder="Inspection Methods"
                          options={INSPECTION_METHODS_OPTIONS}
                          value={INSPECTION_METHODS_OPTIONS.find((o) => o.value === production.inspectionMethods) || null}
                          onChange={(opt) => updateProduction('inspectionMethods', opt?.value ?? '')}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Testing Facilities</span>
                          <div className={`sp-switch ${production.testingFacilities ? 'on' : 'off'}`} role="switch" aria-checked={!!production.testingFacilities} onClick={() => toggle(setProduction, 'testingFacilities')}>
                            <span className="sp-switch-label">{production.testingFacilities ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      {production.testingFacilities && (
                        <div className="col-12">
                          <textarea
                            className="input-area"
                            placeholder="Please Enter Testing Details"
                            value={production.testingFacilitiesDetails ?? ''}
                            onChange={(e) => updateProduction('testingFacilitiesDetails', e.target.value)}
                            rows={4}
                            style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 16px' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="sp-subcard">
                    <h6 className="sp-section-title">
                      <span className="tab-icon me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg>
                      </span>
                      Work Force & Safety
                    </h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input type="number" min="0" className="input-pill" placeholder="Number of Permanent Workers *" value={production.numberPermanentWorkers} onChange={(e) => updateProduction('numberPermanentWorkers', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input type="number" min="0" className="input-pill" placeholder="Number of Contract Workers *" value={production.numberContractWorkers} onChange={(e) => updateProduction('numberContractWorkers', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input type="number" min="0" className="input-pill" placeholder="Number of Seasonal Workers *" value={production.numberSeasonalWorkers} onChange={(e) => updateProduction('numberSeasonalWorkers', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Skilled Worker Ratio" value={production.skilledWorkerRatio} onChange={(e) => updateProduction('skilledWorkerRatio', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Safety Incident in last 12 Months *" value={production.safetyIncidentLast12Months} onChange={(e) => updateProduction('safetyIncidentLast12Months', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Worker Training Program *</span>
                          <div className={`sp-switch ${production.workerTrainingProgram ? 'on' : 'off'}`} role="switch" aria-checked={!!production.workerTrainingProgram} onClick={() => toggle(setProduction, 'workerTrainingProgram')}>
                            <span className="sp-switch-label">{production.workerTrainingProgram ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      {production.workerTrainingProgram && (
                        <div className="col-12">
                          <textarea
                            className="input-area"
                            placeholder="Worker Training program"
                            value={production.workerTrainingProgramDetails ?? ''}
                            onChange={(e) => updateProduction('workerTrainingProgramDetails', e.target.value)}
                            rows={4}
                            style={{ width: '100%', borderRadius: 8, border: '1px solid #e0e0e0', padding: '12px 16px' }}
                          />
                        </div>
                      )}
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Availability Of on site Accommodation</span>
                          <div className={`sp-switch ${production.onSiteAccommodation ? 'on' : 'off'}`} role="switch" aria-checked={!!production.onSiteAccommodation} onClick={() => toggle(setProduction, 'onSiteAccommodation')}>
                            <span className="sp-switch-label">{production.onSiteAccommodation ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input type="number" min="0" className="input-pill" placeholder="No of People who can stay" value={production.noOfPeopleCanStay} onChange={(e) => updateProduction('noOfPeopleCanStay', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Emergency Preparedness *</span>
                          <div className={`sp-switch ${production.emergencyPreparedness ? 'on' : 'off'}`} role="switch" aria-checked={!!production.emergencyPreparedness} onClick={() => toggle(setProduction, 'emergencyPreparedness')}>
                            <span className="sp-switch-label">{production.emergencyPreparedness ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="file-upload-component">
                          <label className="file-box">
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="upload-icon">
                                <path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              </svg>
                              <span className="choose-text">{uploadingEmergencyPlan ? 'Uploading...' : 'Upload emergency Plan Pdf *'}</span>
                            </div>
                            <input type="file" accept=".pdf" onChange={uploadEmergencyPlanPdf} disabled={uploadingEmergencyPlan} style={{ display: 'none' }} />
                          </label>
                          {Array.isArray(production.emergencyPlanDocuments) && production.emergencyPlanDocuments.length > 0 && (
                            <div className="d-flex flex-wrap gap-2 mt-2">
                              {production.emergencyPlanDocuments.map((url, idx) => (
                                <div key={idx} className="d-flex align-items-center gap-1 border rounded px-2 py-1" style={{ fontSize: 12 }}>
                                  <a href={resolvePublicFileUrl(url)} target="_blank" rel="noopener noreferrer" className="text-truncate" style={{ maxWidth: 120 }}>PDF {idx + 1}</a>
                                  <button type="button" className="btn btn-sm btn-danger p-0" style={{ fontSize: 14, lineHeight: 1 }} onClick={() => removeEmergencyPlanDoc(idx)} aria-label="Remove">×</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </TabPane>

            {/* Product Details */}
            <TabPane
              tab={
                <span>
                  <span className="tab-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.27 6.96 8 4.46"/><path d="m12 11.42 8-4.46"/><path d="M3.27 6.96 8 4.46l8-4.46"/><path d="M12 11.42v8.54"/><path d="m20.73 6.96-8 4.46"/><path d="m12 20.42 8-4.46"/></svg>
                  </span>
                  Product Details
                </span>
              }
              key="3"
            >
              <div className="container-fluid">
                <section className="sp-section">
                  <div className="d-flex justify-content-end mb-2 mt-2">
                    <button type="button" className="btn-add" onClick={handleAddProduct} disabled={!productFormCollapsed}>
                      + Add Product
                    </button>
                  </div>
                  {productList.length > 0 && (
                    <div className="sp-subcard mb-3">
                      <h6 className="sp-section-title">📋 Saved Products</h6>
                      <div className="table-responsive">
                        <table className="table product-catalogue-table">
                          <thead>
                            <tr>
                              <th>Product Name</th>
                              <th>Brand</th>
                              <th>Category</th>
                              <th>MOQ</th>
                              <th>Price</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {productList.map((p) => (
                              <tr key={p.id}>
                                <td>{p.productName || p.product_name || '-'}</td>
                                <td>{p.brand || '-'}</td>
                                <td>{categoryData?.find(c => String(c.id) === String(p.productCategoryId || p.category_id))?.name || '-'}</td>
                                <td>{(p.minimumOrderQuantity || p.moq_min) ? `${p.minimumOrderQuantity || p.moq_min} ${p.moqUom || p.moq_uom || ''}` : '-'}</td>
                                <td>{(p.priceAmount || p.price_amount) && (p.priceCurrency || p.price_currency) ? `${p.priceCurrency || p.price_currency} ${p.priceAmount || p.price_amount}` : '-'}</td>
                                <td>
                                  <div className="product-actions">
                                    <button type="button" className="btn-edit" onClick={() => handleEditProduct(p)}>Edit</button>
                                    <button type="button" className="btn-delete" onClick={() => handleDeleteProduct(p.id)}>Delete</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {!productFormCollapsed && (
                    <>
                  {/* Product Details - matching seller profile Product Catalogue form */}
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">📦 Product Details</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Product Name *" value={product.productName} onChange={(e) => updateProduct('productName', e.target.value)} required />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Brand *" value={product.brand} onChange={(e) => updateProduct('brand', e.target.value)} required />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select
                          className="input-pill"
                          value={product.productCategoryId || ''}
                          onChange={handleProductCategoryChange}
                          required
                          disabled={loadingCategories}
                        >
                          <option value="">{loadingCategories ? 'Loading categories...' : 'Product Category *'}</option>
                          {categoryData && categoryData.length > 0 ? (
                            categoryData.map((cat) => (
                              <option key={cat.id} value={String(cat.id)}>{cat.name}</option>
                            ))
                          ) : (
                            !loadingCategories && <option value="" disabled>No categories available</option>
                          )}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select
                          className="input-pill"
                          value={product.productSubCategoryId || ''}
                          onChange={handleProductSubcategoryChange}
                          disabled={!product.productCategoryId || loadingCategories || hookLoadingSubCategories}
                          required
                        >
                          <option value="">{hookLoadingSubCategories ? 'Loading subcategories...' : 'Product Sub-category *'}</option>
                          {hookSubCategories.map((sub) => (
                            <option key={sub.id} value={String(sub.id)}>{sub.name}</option>
                          ))}
                          {hookSubCategories.length === 0 && !hookLoadingSubCategories && (categoryData.find((c) => String(c.id) === String(product.productCategoryId))?.subs || []).map((sub) => (
                            <option key={sub.id} value={String(sub.id)}>{sub.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select
                          className="input-pill"
                          value={product.productSubSubCategoryId || ''}
                          onChange={handleProductSubSubcategoryChange}
                          disabled={!product.productSubCategoryId || loadingCategories || hookLoadingSubSubCategories}
                          required
                        >
                          <option value="">{hookLoadingSubSubCategories ? 'Loading sub-subcategories...' : 'Product Sub-Sub Category *'}</option>
                          {hookSubSubCategories.map((ss) => (
                            <option key={ss.id} value={String(ss.id)}>{ss.name}</option>
                          ))}
                          {hookSubSubCategories.length === 0 && !hookLoadingSubSubCategories && (categoryData.find((c) => String(c.id) === String(product.productCategoryId))?.subs?.find((s) => String(s.id) === String(product.productSubCategoryId))?.subs || []).map((ss) => (
                            <option key={ss.id} value={String(ss.id)}>{ss.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle input-pill-main-products">
                          <span>main products</span>
                          <div
                            className={`sp-switch ${product.mainProduct ? 'on' : 'off'}`}
                            role="switch"
                            aria-checked={!!product.mainProduct}
                            onClick={() => setProduct((prev) => ({ ...prev, mainProduct: !prev.mainProduct }))}
                          >
                            <span className="sp-switch-label">{product.mainProduct ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="HSN Code" value={product.hsnCode} onChange={(e) => updateProduct('hsnCode', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Tax Code" value={product.taxCode} onChange={(e) => updateProduct('taxCode', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Model Number" value={product.modelNumber} onChange={(e) => updateProduct('modelNumber', e.target.value)} />
                      </div>
                      {/* Type of Products & Capacity - moved into Product Details card */}
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Type of Products that can be Manufactured" value={product.typeOfProductsManufactured} onChange={(e) => updateProduct('typeOfProductsManufactured', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4">
                        <ProductionCapacityInput
                          value={product.productionCapacityValue ?? product.productionCapacity ?? ''}
                          unit={product.productionCapacityUnit}
                          period={product.productionCapacityPeriod}
                          onValueChange={(val) => updateProduct('productionCapacityValue', val)}
                          onUnitChange={(val) => updateProduct('productionCapacityUnit', val)}
                          onPeriodChange={(val) => updateProduct('productionCapacityPeriod', val)}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select className="input-pill" value={product.packagingOptions || ''} onChange={(e) => updateProduct('packagingOptions', e.target.value)}>
                          {PACKAGING_OPTIONS_PRODUCT.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <input className="input-pill" placeholder="Material Used" value={product.materialUsed} onChange={(e) => updateProduct('materialUsed', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <select className="input-pill" value={product.productType || ''} onChange={(e) => updateProduct('productType', e.target.value)}>
                          {PRODUCT_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12">
                        <textarea className="input-area" rows={3} placeholder="Product Description *" value={product.productDescription} onChange={(e) => updateProduct('productDescription', e.target.value)} required />
                      </div>
                    </div>

                    {/* Keywords - matching seller profile */}
                    <div className="keywords-section">
                      <h6 className="sp-section-title">🔑 Keywords <span className="required-asterisk">*</span></h6>
                      <div className="product-input-row">
                        <input className="input-pill" placeholder="eg. chilli powder" value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} />
                        <button type="button" className="btn-add" onClick={addKeyword}>+ Add</button>
                      </div>
                      <div className="tags">
                        {(Array.isArray(product.keywords) ? product.keywords : []).map((k) => (
                          <span className="tag" key={k}>{k} <button type="button" className="tag-x" onClick={() => removeKeyword(k)}>×</button></span>
                        ))}
                      </div>
                    </div>

                    {/* Uploads - all three in same row */}
                    <div className="row g-2 uploads-section">
                      <div className="col-12 col-md-4">
                        <div className="file-upload-component">
                          <div className="file-title">Upload the Product catalogue (20*30 px)</div>
                          <label className="file-box">
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
                              <span className="choose-text">{uploadingProductFiles ? 'Uploading...' : 'Choose file'}</span>
                            </div>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleProductCatalogueUpload}
                              disabled={uploadingProductFiles}
                            />
                          </label>
                          {product.productCatalogueUrl && (
                            <div className="mt-2 small text-success">
                              ✓ Uploaded: {product.productCatalogueUrl.split('/').pop()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="file-upload-component">
                          <div className="file-title">Upload Company Logo</div>
                          <label className="file-box">
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
                              <span className="choose-text">{uploadingProductFiles ? 'Uploading...' : 'Choose file'}</span>
                            </div>
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.svg"
                              onChange={handleProductCompanyLogoUpload}
                              disabled={uploadingProductFiles}
                            />
                          </label>
                          {product.productCompanyLogoUrl && (
                            <div className="mt-2 small text-success">
                              ✓ Uploaded: {product.productCompanyLogoUrl.split('/').pop()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <div className="file-upload-component">
                          <div className="file-title">Upload Product Video ( Max 100 MB, MP4, MOV, AVI, MKV, WEBM )</div>
                          <label className="file-box">
                            <div className="file-content">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
                              <span className="choose-text">{uploadingProductFiles ? 'Uploading...' : 'Choose file'}</span>
                            </div>
                            <input
                              type="file"
                              accept=".mp4,.mov,.avi,.mkv,.webm"
                              onChange={handleProductVideoUpload}
                              disabled={uploadingProductFiles}
                            />
                          </label>
                          {product.productVideoUrl && (
                            <div className="mt-2 small text-success">
                              ✓ Uploaded: {product.productVideoUrl.split('/').pop()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Colour Options - above Customization Available, same design as seller product catalogue */}
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">🎨 Colour Options</h6>
                      <button type="button" className="btn-add" onClick={addColourOption}>+ Add More</button>
                    </div>
                    {colourOptionsList.map((option, index) => (
                      <div key={option.id} className={index > 0 ? 'mt-3' : ''}>
                        <div className="row g-2">
                          <div className="col-12 col-md-6 col-lg-3">
                            <input
                              className="input-pill"
                              placeholder="#ED00BE"
                              value={option.color || ''}
                              readOnly
                            />
                          </div>
                          <div className="col-12 col-md-2 col-lg-1">
                            <div className="input-pill color-picker-pill">
                              <input
                                type="color"
                                className="color-input"
                                value={option.color || '#ED00BE'}
                                onChange={(e) => updateColourOption(option.id, 'color', e.target.value)}
                                aria-label="Pick colour"
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6 col-lg-7">
                            <div className="file-upload-component">
                              <label className="file-box">
                                <div className="file-content">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
                                  <span className="choose-text">Upload color image (206*206px, max 2MB)</span>
                                </div>
                                <input
                                  type="file"
                                  accept=".jpg,.jpeg,.png"
                                  onChange={(e) => handleColourOptionImageUpload(option.id, e)}
                                  disabled={uploadingProductFiles}
                                />
                              </label>
                              {option.imageUrl && (
                                <div className="mt-2 small text-success">
                                  ✓ Uploaded: {option.imageUrl.split('/').pop()}
                                </div>
                              )}
                            </div>
                          </div>
                          {colourOptionsList.length > 1 && (
                            <div className="col-12 col-md-1 col-lg-1">
                              <button
                                type="button"
                                className="btn-delete"
                                onClick={() => removeColourOption(option.id)}
                                title="Remove"
                              >
                                ×
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="sp-subcard">
                    <div className="d-flex align-items-center mb-3 sp-section-header">
                      <h6 className="sp-section-title">🛠️ Customization Available</h6>
                      <div
                        className={`sp-switch ${product.customizationAvailable ? 'on' : 'off'}`}
                        role="switch"
                        aria-checked={!!product.customizationAvailable}
                        onClick={() => setProduct((prev) => ({ ...prev, customizationAvailable: !prev.customizationAvailable }))}
                      >
                        <span className="sp-switch-label">{product.customizationAvailable ? 'Yes' : 'No'}</span>
                        <span className="sp-switch-thumb"></span>
                      </div>
                    </div>
                    {product.customizationAvailable && (
                      <>
                        <div className="row g-2">
                          <div className="col-12">
                            <Select
                              className="input-pill p-0"
                              placeholder="Select Type of Customization"
                              options={customizationTypesOptionsList}
                              onChange={(opt) => updateProduct('customizationType', opt?.value ?? '')}
                              value={findOption(customizationTypesOptionsList, product.customizationType)}
                              isClearable
                            />
                          </div>
                        </div>
                        {product.customizationType && (
                          <div className="customization-grid mt-4">
                            <div className="cg-header">Specification of Customization</div>
                            <div className="cg-header text-center">YES / NO</div>
                            <div className="cg-header text-center">MOQ</div>
                            <div className="cg-header text-center">Price (Without Tax)</div>
                            <div className="cg-header text-center">Tax %</div>
                            <div className="cg-header text-center">Price (With Tax)</div>
                            <div className="cg-header text-center">Description/image</div>
                            {customizationRows
                              .filter(row => getCustomizationVisibleRows(product.customizationType).includes(row.id))
                              .map((row) => (
                                <React.Fragment key={row.id}>
                                  <div className="cg-cell"><input className="input-pill" value={row.name} readOnly /></div>
                                  <div className="cg-cell text-center">
                                    <div
                                      className={`sp-switch ${row.enabled ? 'on' : 'off'}`}
                                      role="switch"
                                      aria-checked={!!row.enabled}
                                      onClick={() => toggleCustomizationRow(row.id)}
                                    >
                                      <span className="sp-switch-label">{row.enabled ? 'Yes' : 'No'}</span>
                                      <span className="sp-switch-thumb"></span>
                                    </div>
                                  </div>
                                  <div className="cg-cell">{renderCustomizationInput(row, 'moq')}</div>
                                  <div className="cg-cell">{renderCustomizationInput(row, 'priceNoTax')}</div>
                                  <div className="cg-cell">{renderCustomizationInput(row, 'tax')}</div>
                                  <div className="cg-cell">{renderCustomizationInput(row, 'priceWithTax')}</div>
                                  <div className="cg-cell">{renderCustomizationDescCell(row)}</div>
                                </React.Fragment>
                              ))}
                          </div>
                        )}
                      </>
                    )}
                    <Modal
                      title="Customization"
                      open={descModal.visible}
                      onCancel={() => setDescModal({ visible: false, rowId: null })}
                      footer={[
                        <button key="cancel" type="button" className="btn cus-btn-outline-primary" onClick={() => setDescModal({ visible: false, rowId: null })}>Cancel</button>,
                        <button key="save" type="button" className="btn cus-btn-add" onClick={() => { if (descModal.rowId) updateCustomizationField(descModal.rowId, 'desc', descText); setDescModal({ visible: false, rowId: null }); }}>Send</button>,
                      ]}
                    >
                      <div className="custom-desc-modal">
                        <div className="desc-box">
                          <div className="desc-label">Description</div>
                          <textarea id="custom-desc-area" className="input-area" rows={6} placeholder="Writing entries: 100 words min, 1000 words max" value={descText} onChange={(e) => setDescText(e.target.value)} />
                          <hr className="desc-sep" />
                          <div className="file-upload-component">
                            <div className="input-pill upload-pill" onClick={(e) => { const input = e.currentTarget.querySelector('input[type=file]'); if (input) input.click(); }}>
                              <span className="upload-icon-only" aria-hidden>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="upload-icon"><path d="M12 3v12" /><path d="m17 8-5-5-5 5" /><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /></svg>
                              </span>
                              <span className="choose-text ms-2">Upload Documents / Images For Customization Reference (max 1MB) ({descFiles.length}/3)</span>
                              <input
                                type="file"
                                className="d-none"
                                accept=".jpg,.jpeg,.png,.pdf"
                                multiple
                                onChange={(e) => setDescFiles(Array.from(e.target.files || []).slice(0, 3))}
                              />
                            </div>
                            {descFiles.length > 0 && (
                              <div className="upload-list">
                                {descFiles.map((f, idx) => (
                                  <span key={idx} className="upload-chip">{f.name}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Modal>
                  </div>

                  {/* Dimensions - above MOQ, same design as seller product catalogue */}
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">📐 Dimensions</h6>
                      <button type="button" className="btn-add" onClick={addDimension}>+ Add More</button>
                    </div>
                    {dimensionsList.map((dim, index) => (
                      <div key={dim.id} className={index > 0 ? 'mt-3' : ''}>
                        <div className="row g-2 align-items-center">
                          <div className="col-6 col-md-4 col-lg-2">
                            <input
                              className="input-pill"
                              placeholder="Height"
                              value={dim.height || ''}
                              onChange={(e) => updateDimension(dim.id, 'height', e.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-4 col-lg-2">
                            <input
                              className="input-pill"
                              placeholder="Length"
                              value={dim.length || ''}
                              onChange={(e) => updateDimension(dim.id, 'length', e.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-4 col-lg-2">
                            <input
                              className="input-pill"
                              placeholder="Width"
                              value={dim.width || ''}
                              onChange={(e) => updateDimension(dim.id, 'width', e.target.value)}
                            />
                          </div>
                          <div className="col-6 col-md-4 col-lg-2">
                            <input
                              className="input-pill"
                              placeholder="Weight"
                              value={dim.weight || ''}
                              onChange={(e) => updateDimension(dim.id, 'weight', e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-4 col-lg-4">
                            <div className="d-flex align-items-center gap-2">
                              <Select
                                className="input-pill p-0 flex-grow-1"
                                placeholder="Unit of Measurements"
                                options={dimensionUnitOptionsList}
                                onChange={(opt) => updateDimension(dim.id, 'uom', opt?.value ?? '')}
                                value={findOption(dimensionUnitOptionsList, dim.uom)}
                                isClearable
                              />
                              {dimensionsList.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => removeDimension(dim.id)}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* MOQ - same layout as seller profile product catalogue */}
                  <div className="sp-subcard">
                    <h6 className="sp-section-title">📦 MOQ <span className="required-asterisk">*</span></h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-2">
                        <input
                          className="input-pill"
                          placeholder="Minimum Order Quantity *"
                          value={product.minimumOrderQuantity || ''}
                          onChange={(e) => updateProduct('minimumOrderQuantity', e.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <Select
                          className="input-pill p-0"
                          placeholder="Unit of Measurements *"
                          options={dimensionUnitOptionsList}
                          onChange={(opt) => updateProduct('moqUom', opt?.value || '')}
                          value={findOption(dimensionUnitOptionsList, product.moqUom)}
                          isClearable
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <input
                          className="input-pill"
                          placeholder="Price (Without Tax) *"
                          type="number"
                          step="0.01"
                          value={product.moqPriceNotax || ''}
                          onChange={(e) => {
                            const priceWithoutTax = e.target.value;
                            const taxPercent = parseFloat(product.moqTax) || 0;
                            const priceWithTax = priceWithoutTax && taxPercent ? (parseFloat(priceWithoutTax) * (1 + taxPercent / 100)).toFixed(2) : '';
                            updateProduct('moqPriceNotax', priceWithoutTax);
                            setProduct((prev) => ({ ...prev, moqPriceTax: priceWithTax }));
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <input
                          className="input-pill"
                          placeholder="Tax (%) *"
                          type="number"
                          step="0.01"
                          value={product.moqTax || ''}
                          onChange={(e) => {
                            const taxPercent = e.target.value;
                            const priceWithoutTax = parseFloat(product.moqPriceNotax) || 0;
                            const priceWithTax = taxPercent && priceWithoutTax ? (priceWithoutTax * (1 + parseFloat(taxPercent) / 100)).toFixed(2) : '';
                            updateProduct('moqTax', taxPercent);
                            setProduct((prev) => ({ ...prev, moqPriceTax: priceWithTax }));
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <input
                          className="input-pill"
                          placeholder="Price (With Tax) *"
                          value={product.moqPriceTax || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="col-12 col-md-2">
                        <Select
                          className="input-pill p-0"
                          placeholder="Currency *"
                          options={currencyOptions || []}
                          onChange={(opt) => updateProduct('moqCurrency', opt?.value || '')}
                          value={findOption(currencyOptions || [], product.moqCurrency)}
                          isClearable
                        />
                      </div>
                    </div>
                  </div>

                  {/* Lead Time - same layout as seller profile product catalogue */}
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">⏱️ Lead Time</h6>
                      <button type="button" className="btn-add" onClick={addLeadTime}>+ Add More</button>
                    </div>
                    {leadTimeList.map((lead, index) => (
                      <div key={lead.id} className={index > 0 ? 'mt-3' : ''}>
                        <div className="row g-2">
                          <div className="col-12 col-md-4">
                            <input
                              type="number"
                              className="input-pill"
                              placeholder="Lead Time (in Days)"
                              value={lead.days || ''}
                              onChange={(e) => updateLeadTime(lead.id, 'days', e.target.value)}
                              min="0"
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <input
                              className="input-pill"
                              placeholder="Min Quantity"
                              value={lead.min || ''}
                              onChange={(e) => updateLeadTime(lead.id, 'min', e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                className="input-pill flex-grow-1"
                                placeholder="Max Quantity"
                                value={lead.max || ''}
                                onChange={(e) => updateLeadTime(lead.id, 'max', e.target.value)}
                              />
                              {leadTimeList.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => removeLeadTime(lead.id)}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pricing & Commercial - matches design: icon, Row1 (Pricing Model, Price+Currency, UOM, Incoterms, Payment Terms), Row2 (Security Deposit, Renewal, Termination, Maintenance), Row3 (Utilities, Property Tax, Escalation, Add on), Row4 (Is Price Negotiable) */}
                  <div className="sp-subcard">
                    <h6 className="sp-section-title"><span style={{ marginRight: 6 }} aria-hidden>💰</span> Pricing & Commercial</h6>
                    <div className="row g-2">
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Choose Pricing Model"
                          options={PRICING_MODEL_OPTIONS}
                          onChange={(opt) => {
                            updateProduct('pricingModel', opt?.value || '');
                            updateProduct('priceUom', '');
                          }}
                          value={findOption(PRICING_MODEL_OPTIONS, product.pricingModel)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-6">
                        <div className="input-group-prefix-suffix input-group-row cm-number-unit cm-price-amount">
                          <input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            step="0.01"
                            className="input-middle input-middle-with-border"
                            placeholder="Price Amount"
                            value={product.priceAmount || ''}
                            onChange={(e) => updateProduct('priceAmount', e.target.value)}
                            aria-label="Price Amount"
                          />
                          <div className="input-prefix input-prefix-no-borders input-unit-suffix cm-price-currency">
                            <select
                              value={product.priceCurrency || ''}
                              onChange={(e) => updateProduct('priceCurrency', e.target.value)}
                              aria-label="Currency"
                            >
                              <option value="">$</option>
                              {(currencyOptions || []).map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="input-prefix input-prefix-no-borders input-unit-suffix cm-price-uom">
                            <select
                              value={product.priceUom || ''}
                              onChange={(e) => updateProduct('priceUom', e.target.value)}
                              aria-label="Unit of Measure"
                            >
                              <option value="">UOM</option>
                              {priceUomOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Incoterms"
                          options={INCOTERMS_OPTIONS}
                          onChange={(opt) => updateProduct('incoterms', opt?.value || '')}
                          value={findOption(INCOTERMS_OPTIONS, product.incoterms)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="w-100" aria-hidden="true" />
                      {product.pricingModel === 'cost_bom_pass_through_markup' && (
                        <div className="col-12 col-md-6 col-lg-3">
                          <Select
                            className="input-pill p-0"
                            placeholder="Markup Basis"
                            options={MARKUP_BASIS_OPTIONS}
                            onChange={(opt) => updateProduct('markupBasis', opt?.value || '')}
                            value={findOption(MARKUP_BASIS_OPTIONS, product.markupBasis)}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Payment Terms"
                          options={PAYMENT_TERMS_OPTIONS}
                          onChange={(opt) => updateProduct('paymentTerms', opt?.value || '')}
                          value={findOption(PAYMENT_TERMS_OPTIONS, product.paymentTerms)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Security Deposit</span>
                          <div className={`sp-switch ${product.securityDeposit ? 'on' : 'off'}`} role="switch" aria-checked={!!product.securityDeposit} onClick={() => toggle(setProduct, 'securityDeposit')}>
                            <span className="sp-switch-label">{product.securityDeposit ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Renewal Terms"
                          options={RENEWAL_TERMS_OPTIONS}
                          onChange={(opt) => updateProduct('renewalTerms', opt?.value || '')}
                          value={findOption(RENEWAL_TERMS_OPTIONS, product.renewalTerms)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Termination Terms"
                          options={TERMINATION_TERMS_OPTIONS}
                          onChange={(opt) => updateProduct('terminationTerms', opt?.value || '')}
                          value={findOption(TERMINATION_TERMS_OPTIONS, product.terminationTerms)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Maintenance Responsibility"
                          options={RESPONSIBILITY_OPTIONS}
                          onChange={(opt) => updateProduct('maintenanceResponsibility', opt?.value || '')}
                          value={findOption(RESPONSIBILITY_OPTIONS, product.maintenanceResponsibility)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Utilities Responsibility"
                          options={RESPONSIBILITY_OPTIONS}
                          onChange={(opt) => updateProduct('utilitiesResponsibility', opt?.value || '')}
                          value={findOption(RESPONSIBILITY_OPTIONS, product.utilitiesResponsibility)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Property Tax Responsibility"
                          options={RESPONSIBILITY_OPTIONS}
                          onChange={(opt) => updateProduct('propertyTaxResponsibility', opt?.value || '')}
                          value={findOption(RESPONSIBILITY_OPTIONS, product.propertyTaxResponsibility)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Escalation Basis"
                          options={ESCALATION_BASIS_OPTIONS}
                          onChange={(opt) => updateProduct('escalationBasis', opt?.value || '')}
                          value={findOption(ESCALATION_BASIS_OPTIONS, product.escalationBasis)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <Select
                          className="input-pill p-0"
                          placeholder="Add on Categories"
                          options={ADD_ON_CATEGORIES_OPTIONS}
                          onChange={(opt) => updateProduct('addOnCategories', opt?.value || '')}
                          value={findOption(ADD_ON_CATEGORIES_OPTIONS, product.addOnCategories)}
                          isClearable
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="input-pill input-pill-toggle">
                          <span>Is Price Negotiable</span>
                          <div className={`sp-switch ${product.isPriceNegotiable ? 'on' : 'off'}`} role="switch" aria-checked={!!product.isPriceNegotiable} onClick={() => toggle(setProduct, 'isPriceNegotiable')}>
                            <span className="sp-switch-label">{product.isPriceNegotiable ? 'Yes' : 'No'}</span>
                            <span className="sp-switch-thumb"></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* FAQ - same as seller profile product catalogue */}
                  <div className="sp-subcard">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="sp-section-title">❓ FAQ</h6>
                      <button type="button" className="btn-add" onClick={addFAQ}>+ Add More</button>
                    </div>
                    {faqList.map((faq, index) => (
                      <div key={faq.id} className={index > 0 ? 'mt-3' : ''}>
                        <div className="row g-2">
                          <div className="col-12 col-md-6">
                            <input
                              className="input-pill"
                              placeholder="Question"
                              value={faq.question || ''}
                              onChange={(e) => updateFAQ(faq.id, 'question', e.target.value)}
                            />
                          </div>
                          <div className="col-12 col-md-6">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                className="input-pill flex-grow-1"
                                placeholder="Answer"
                                value={faq.answer || ''}
                                onChange={(e) => updateFAQ(faq.id, 'answer', e.target.value)}
                              />
                              {faqList.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => removeFAQ(faq.id)}
                                  title="Remove"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="sp-actions-row mb-2 mt-3">
                    <div className="sp-left-actions">
                      <button type="button" className="btn btn-outline-primary" onClick={handleSaveCurrentProduct}>Back to list</button>
                    </div>
                    <div className="sp-right-actions">
                      <button type="button" className="btn btn-primary" onClick={handleSaveCurrentProduct}>
                        {editingProductId ? 'Update Product' : 'Save Product'}
                      </button>
                    </div>
                  </div>
                    </>
                  )}
                </section>
              </div>
            </TabPane>
          </Tabs>

          <div className="sp-actions-row mb-2 mt-3">
            <div className="sp-left-actions">
              {activeTab !== '1' && !(activeTab === '3' && !productFormCollapsed) && (
                <button type="button" className="btn btn-outline-primary sp-back" onClick={() => setActiveTab(activeTab === '3' ? '2' : '1')}>Back</button>
              )}
            </div>
            <div className="sp-right-actions">
              {activeTab === '1' && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveFactoryDetails}
                    disabled={savingFactoryDetails}
                  >
                    {savingFactoryDetails ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary ms-2"
                    onClick={handleFactoryDetailsNext}
                  >
                    Next
                  </button>
                </>
              )}
              {activeTab === '2' && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveProductionDetails}
                    disabled={savingProductionDetails}
                  >
                    {savingProductionDetails ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary ms-2"
                    onClick={() => setActiveTab('3')}
                    disabled={!productionDetailsSaved}
                  >
                    Next
                  </button>
                </>
              )}
              {activeTab === '3' && (
                <button type="button" className="btn btn-primary" onClick={handleSaveProductDetails} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
